import {
    Box,
    Typography,
    Card,
    CardMedia,
    CardContent,
    useTheme,
    useMediaQuery,
    Tabs,
    Tab,
    IconButton,
    Collapse,
    Radio,
    RadioGroup,
    FormControlLabel,
    TextField,
    Button,
    Breadcrumbs,
    Tooltip,
    Paper,
    Grid,
} from '@mui/material';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import ProductModal from './ProductModal';
import {
    getProductListSimple,
    getProductDetail,
    getDimEntities,
    type Product,
    type Category,
    type DimEntitiesResponse,
} from '@/services/matchingWeb.service';
import { getCategories } from '@/services/category.service';
import {
    postDDMLiveScheduleProducts,
    deleteDDMLiveScheduleProduct,
    getDDMLiveSchedule,
    getDDMLiveScheduleOne,
    type ScheduleResponse,
} from '@/services/matching.service';
import useDDMCategoryStore from '@/externals/ddm/store/categoryStore';
import { RequestQueryBuilder } from '@nestjsx/crud-request';
import { MATERIAL_OPTIONS } from '@/constant/materialConfig';
import {
    DDM_BUILDING_FLOORS,
    NMD_BUILDING_FLOORS,
    BUSAN_BUILDING_FLOORS,
    BUILDINGS,
    getBuildingName,
    getRegionName,
} from '@/constant/buildingConfig';
import { toast } from 'react-toastify';

import { COUNTRIES, SIZES_ALPHA, SIZES_NUMERIC } from '@/constant/catalogFilterConfig';
import { useTranslation } from 'react-i18next';
import { useSessionStore } from '@/store';
import { dialogOpen } from '@/modules/MuiDialog';
import LoginDialog from '@/pages/dialogs/LoginDialog';
import { COLOR_PALETTE } from '@/externals/ddm/constants/colorTypeConfig';
import {
    getLocalizedGroupTitle,
    getLocalizedNameFromI18n,
    translateCategoryName,
    parseStoreLocation,
} from '@/utils/formatter';
import ddmLoadingGif from '@/assets/images/logo/ddm_loading.gif';
import storeIcon from '@/assets/images/sources/store.png';
import buildingIcon from '@/assets/images/sources/building.png';

type ProductWithImageSizes = Product & {
    representImageUrlSizes?: {
        origin?: string;
        small?: string;
        medium?: string;
        large?: string;
    };
};

// 카테고리 이미지 import
import womenAccessories from '@/assets/images/categories/women/women_accessories.png';
import womenBags from '@/assets/images/categories/women/women_bags.png';
import womenClothing from '@/assets/images/categories/women/women_clothing.png';
import womenJewelry from '@/assets/images/categories/women/women_jewelry.png';
import womenShoes from '@/assets/images/categories/women/women_shoes.png';

import menAccessories from '@/assets/images/categories/men/men_accessories.png';
import menBags from '@/assets/images/categories/men/men_bags.png';
import menClothing from '@/assets/images/categories/men/men_clothing.png';
import menShoes from '@/assets/images/categories/men/men_shoes.png';

import kidsAccessories from '@/assets/images/categories/kids/kids_accessories.png';
import kidsClothing from '@/assets/images/categories/kids/kids_clothing.png';
import kidsShoes from '@/assets/images/categories/kids/kids_shoes.png';

// 카테고리 이미지 매핑 객체
const categoryImageMap: { [key: string]: string } = {
    // Women
    women_clothing: womenClothing,
    women_shoes: womenShoes,
    women_bags: womenBags,
    women_jewelry: womenJewelry,
    women_accessories: womenAccessories,
    // Men
    men_clothing: menClothing,
    men_shoes: menShoes,
    men_bags: menBags,
    men_accessories: menAccessories,
    // Kids
    kids_clothing: kidsClothing,
    kids_shoes: kidsShoes,
    kids_accessories: kidsAccessories,
};

// CategoryData 인터페이스
interface CategoryData {
    [key: string]: {
        name: string;
        categories: {
            name: string;
            icon: string;
            subcategories: string[];
        }[];
    };
}

// API 응답을 UI에 맞게 변환하는 함수
const transformApiToUiProduct = (apiProduct: ProductWithImageSizes, t: any, language: string) => {
    const thumbnail =
        apiProduct.representImageUrlSizes?.medium ||
        apiProduct.representImageUrlSizes?.origin ||
        apiProduct.representImageUrl;

    const representMatchingProduct = apiProduct.matchingProducts?.find(
        (product) => product.id === apiProduct.representProductId,
    );

    return {
        id: apiProduct.id,
        image: thumbnail,
        title: getLocalizedGroupTitle(apiProduct, language) || apiProduct.groupTitle,
        price: representMatchingProduct?.supplyPrice || 0,
        category: t('catalog.turtleneckLongSleeve'), // TODO: 실제 카테고리 매핑 필요
        likes: Math.floor(Math.random() * 100), // TODO: 실제 좋아요 수 API 연동 필요
    };
};

const RootCatalog = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { userId, hasSession } = useSessionStore();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    // 층수 변환 함수 (지하1층 -> B1층, 지하2층 -> B2층)
    const convertFloorToApiFormat = (floor: string): string => {
        if (floor === '전체') return floor;
        if (floor.startsWith('지하')) {
            const floorNumber = floor.replace('지하', '').replace('층', '');
            return `B${floorNumber}층`;
        }
        return floor;
    };

    // URL 쿼리스트링에서 scheduleId 추출

    // 스케줄 데이터 상태
    const [scheduleId, setScheduleId] = useState('');
    const [scheduledPidSet, setScheduledPidSet] = useState<Set<string>>(new Set());
    const [scheduleData, setScheduleData] = useState<ScheduleResponse | null>(null);

    // 사이드바 존재 여부에 따른 그리드 컬럼 수 계산
    const getGridColumns = () => {
        if (isMobile) return 2;
        if (isTablet) return 4;
        if (isDesktop) return 5; // 데스크톱에서는 5개씩 표시
        return 5;
    };

    const gridColumns = getGridColumns();

    // 모달 상태
    const [open, setOpen] = useState(false);
    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

    // 무한 스크롤 상태
    const [products, setProducts] = useState<Product[]>([]);
    const [page, setPage] = useState(1);
    const [productTotal, setProductTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const sidebarRef = useRef<HTMLDivElement | null>(null);
    const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);
    const isFetchingNextPageRef = useRef(false);
    const isInitialLoadRef = useRef(true); // 첫 로딩 여부 추적
    const [isBottomReached, setIsBottomReached] = useState(false);

    // 카테고리 탭 상태
    const [selectedTab, setSelectedTab] = useState(0);
    const [subCategories, setSubCategories] = useState<Category[]>([]);
    const [categoriesLoading, setCategoriesLoading] = useState(false);

    // 3depth 카테고리 상태 추가
    const [allCategories, setAllCategories] = useState<Category[]>([]);
    const [currentSubCategories, setCurrentSubCategories] = useState<Category[]>([]);
    const [selectedSubTab, setSelectedSubTab] = useState(0);

    // 4depth 카테고리 상태 추가
    const [currentSubSubCategories, setCurrentSubSubCategories] = useState<Category[]>([]);
    const [selectedSubSubTab, setSelectedSubSubTab] = useState(0);

    // 현재 선택된 성별 상태 추가
    const [selectedGender, setSelectedGender] = useState<'woman' | 'men' | 'kids'>('woman');

    // 필터 상태
    const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
    const [selectedPatterns, setSelectedPatterns] = useState<string[]>([]);
    const [selectedStyles, setSelectedStyles] = useState<string[]>([]);
    const [selectedColors, setSelectedColors] = useState<string[]>([]);
    const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
    const [selectedSizesAlpha, setSelectedSizesAlpha] = useState<string[]>([]);
    const [selectedSizesNumeric, setSelectedSizesNumeric] = useState<string[]>([]);

    // 새로운 필터 상태들
    const [selectedDetails, setSelectedDetails] = useState<string[]>([]);
    const [selectedFits, setSelectedFits] = useState<string[]>([]);
    const [selectedDimPatterns, setSelectedDimPatterns] = useState<string[]>([]);
    const [selectedSeasons, setSelectedSeasons] = useState<string[]>([]);
    const [selectedSheerness, setSelectedSheerness] = useState<string[]>([]);
    const [selectedStretch, setSelectedStretch] = useState<string[]>([]);
    const [selectedDimStyles, setSelectedDimStyles] = useState<string[]>([]);
    const [selectedDimThickness, setSelectedDimThickness] = useState<string[]>([]);

    // 가격 필터
    const [priceRange, setPriceRange] = useState('all');
    const [customMinPrice, setCustomMinPrice] = useState('');
    const [customMaxPrice, setCustomMaxPrice] = useState('');

    // 낱장가능 필터
    const [singleSheetAvailable, setSingleSheetAvailable] = useState(false);

    const getDimEntityLabel = useCallback(
        (entity: { name: string; nameI18n?: Record<string, string> } | null | undefined) =>
            getLocalizedNameFromI18n(entity, i18n.language),
        [i18n.language],
    );

    // 검색 필터
    const searchKeywordRef = useRef<HTMLInputElement>(null);
    const [appliedSearchKeywords, setAppliedSearchKeywords] = useState<string[]>([]);
    const [searchMode, setSearchMode] = useState<'include' | 'exclude'>('include');

    // 더보기 상태
    const [showAllMaterials, setShowAllMaterials] = useState(false);
    const [showAllPatterns, setShowAllPatterns] = useState(false);
    const [showAllStyles, setShowAllStyles] = useState(false);
    const [showAllColors, setShowAllColors] = useState(false);
    const [showAllCountries, setShowAllCountries] = useState(false);
    const [showAllBuildings, setShowAllBuildings] = useState(false);

    // 새로운 필터 더보기 상태들
    const [showAllDetails, setShowAllDetails] = useState(false);
    const [showAllFits, setShowAllFits] = useState(false);
    const [showAllDimPatterns, setShowAllDimPatterns] = useState(false);
    const [showAllSeasons, setShowAllSeasons] = useState(false);
    const [showAllSheerness, setShowAllSheerness] = useState(false);
    const [showAllStretch, setShowAllStretch] = useState(false);
    const [showAllDimStyles, setShowAllDimStyles] = useState(false);
    const [showAllDimThickness, setShowAllDimThickness] = useState(false);

    // 건물 탭 및 정렬 상태
    const [selectedBuildingTab, setSelectedBuildingTab] = useState(0); // 0: 동대문, 1: 남대문, 2: 부산
    const [buildingSortOrder, setBuildingSortOrder] = useState<'recommended' | 'alphabetical'>('recommended');

    // 건물별 층 선택 상태
    const [expandedBuildingFloors, setExpandedBuildingFloors] = useState<string | null>(null);
    const [buildingFloorSelections, setBuildingFloorSelections] = useState<Record<string, string[]>>({});

    // 사이드바 펼침 상태
    const [sidebarExpanded, setSidebarExpanded] = useState({
        color: true,
        detail: true,
        pattern: true,
        style: true,
        price: true,
        country: true,
        size: true,
        building: true,
    });

    // 찜하기 상태 (로컬)
    const [favorites, setFavorites] = useState<Set<string>>(new Set());

    // DimEntities 데이터 상태
    const [dimEntities, setDimEntities] = useState<DimEntitiesResponse | null>(null);
    const [isInitialDataLoaded, setIsInitialDataLoaded] = useState(false); // 초기 데이터 로딩 완료 플래그
    const [isCategoryDataProcessed, setIsCategoryDataProcessed] = useState(false); // 카테고리 데이터 처리 완료 플래그

    // 카테고리 드롭다운 상태
    const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
    const [hoveredGender, setHoveredGender] = useState<string | null>('woman');
    const [categoryData, setCategoryData] = useState<CategoryData>({});
    const [categoryLoading, setCategoryLoading] = useState(false);
    const categoryButtonRef = useRef<HTMLButtonElement | null>(null);
    const categoryDropdownRef = useRef<HTMLDivElement | null>(null);

    const fetchAllCategories = useDDMCategoryStore((state) => state.fetchAllCategories);
    const subCategoriesFromStore = useDDMCategoryStore((state) => state.subCategories);
    const mainCategoriesFromStore = useDDMCategoryStore((state) => state.mainCategories);

    // 로그인 체크 함수
    const checkLoginAndShowModal = (callback: () => void) => {
        if (!hasSession) {
            dialogOpen({
                innerElement: <LoginDialog onLoginSuccess={callback} />,
                maxWidth: false,
                sx: {
                    '& .MuiDialog-paper': {
                        maxWidth: '420px',
                        width: '100%',
                    },
                },
            });
            return false;
        }
        return true;
    };

    // scheduleData 재로드 함수
    const reloadScheduleData = async () => {
        if (!scheduleId || !userId) return;

        try {
            const data = await getDDMLiveScheduleOne({
                userId,
                scheduleId,
            });
            setScheduleData(data);
        } catch (error) {
            console.error('Failed to reload schedule data:', error);
        }
    };

    // 스케줄 액션 처리 함수
    const handleScheduleAction = async (productId: string, isSelected: boolean, skipScheduleIdCheck = false) => {
        if (!skipScheduleIdCheck && !scheduleId) {
            toast.error(t('catalog.scheduleIdRequired'));
            return;
        }

        try {
            if (isSelected) {
                setScheduledPidSet((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(productId);
                    return newSet;
                });
                await deleteDDMLiveScheduleProduct({
                    userId: userId,
                    scheduleId: scheduleId,
                    mapperId: productId,
                });
                toast.success(t('catalog.productRemovedFromSchedule'));

                // scheduleData 재로드
                await reloadScheduleData();
            } else {
                // 상품 추가 로직
                const prevSet = new Set(scheduledPidSet);
                try {
                    prevSet.add(productId);
                    setScheduledPidSet(prevSet);
                    await postDDMLiveScheduleProducts({
                        userId: userId,
                        scheduleId: scheduleId,
                        productGroupId: productId,
                    });
                    toast.success(t('catalog.productAddedToSchedule'));

                    // scheduleData 재로드
                    await reloadScheduleData();
                } catch (error) {
                    console.error('상품 추가 실패:', error);
                    setScheduledPidSet(prevSet);
                }
            }
        } catch (error) {
            console.error('상품 처리 실패:', error);
            toast.error(t('catalog.productProcessFailed'));
        }
    };

    // 성별별 카테고리 ID 매핑 (API에서 동적으로 가져옴)
    const [genderCategoryIds, setGenderCategoryIds] = useState<{
        woman: string | null;
        men: string | null;
        kids: string | null;
    }>({
        woman: null,
        men: null,
        kids: null,
    });

    // 카테고리 데이터 로딩 완료 여부
    const [categoriesReady, setCategoriesReady] = useState(false);

    const getGenderCategoryId = (gender: 'woman' | 'men' | 'kids') => {
        return genderCategoryIds[gender];
    };

    // 카테고리 계층 구조 파싱 및 상태 관리
    const parseCategoryHierarchy = () => {
        if (subCategoriesFromStore.length === 0) return;

        // 선택된 성별의 카테고리 ID 가져오기
        const genderCategoryId = getGenderCategoryId(selectedGender);

        // 성별 카테고리 ID가 로드되지 않았으면 대기
        if (!genderCategoryId) {
            return;
        }

        const directSubCategories = subCategoriesFromStore
            .filter((cat) => cat.parentCategoryId === genderCategoryId)
            .sort((a, b) => a.sortOrder - b.sortOrder);
        setSubCategories(directSubCategories);
        setAllCategories(subCategoriesFromStore);
    };

    // 카테고리 클릭 핸들러
    const handleCategoryClick = (category: Category, index: number) => {
        try {
            setSelectedTab(index);
            // 선택된 카테고리의 하위 카테고리들을 찾아서 설정
            const subCategoriesOfSelected =
                allCategories
                    ?.filter((cat) => cat.parentCategoryId === category.categoryId)
                    .sort((a, b) => a.sortOrder - b.sortOrder) || [];
            setCurrentSubCategories(subCategoriesOfSelected);
            setSelectedSubTab(0); // 서브 탭 초기화
            setCurrentSubSubCategories([]);
            setSelectedSubSubTab(0);
        } catch (error) {
            console.error('Error in handleCategoryClick:', error);
        }
    };

    const handleSubCategoryClick = (index: number) => {
        try {
            setSelectedSubTab(index);
            // 선택된 서브 카테고리의 하위 카테고리들을 찾아서 설정 (4depth)
            const subCategory = currentSubCategories[index - 1];
            if (subCategory) {
                const subSubCategoriesOfSelected =
                    allCategories
                        ?.filter((cat) => cat.parentCategoryId === subCategory.categoryId)
                        .sort((a, b) => a.sortOrder - b.sortOrder) || [];
                setCurrentSubSubCategories(subSubCategoriesOfSelected);
                setSelectedSubSubTab(0); // 서브서브 탭 초기화
            }
        } catch (error) {
            console.error('Error in handleSubCategoryClick:', error);
        }
    };

    const handleSubSubCategoryClick = (index: number) => {
        try {
            setSelectedSubSubTab(index);
        } catch (error) {
            console.error('Error in handleSubSubCategoryClick:', error);
        }
    };

    // scheduleId 유무에 따라 스케줄 데이터 로드
    useEffect(() => {
        (async () => {
            if (!userId) return;

            try {
                const _scheduleId = searchParams.get('scheduleId');
                let _scheduleData;
                if (_scheduleId) {
                    // scheduleId가 있으면 getDDMLiveScheduleOne 호출
                    const data = await getDDMLiveScheduleOne({ userId, scheduleId: _scheduleId });
                    setScheduleId(_scheduleId);
                    setScheduleData(data);
                    _scheduleData = data;
                } else {
                    const qb = RequestQueryBuilder.create();
                    qb.setFilter({ field: 'activatedAt', operator: '$notnull' });
                    const data = await getDDMLiveSchedule({ userId, query: qb.query() });
                    if (data && data.length > 0) {
                        const firstScheduleId = data[0].id;
                        setScheduleId(firstScheduleId);
                        setScheduleData(data[0]);
                        _scheduleData = data[0];
                    }
                }
            } catch (error) {
                console.error('Failed to load schedule data:', error);
            }
        })();
    }, [userId]);

    const [selectedVendor, setSelectedVendor] = useState<Set<string>>(new Set());
    const selectedVendorLimit = 5;
    useEffect(() => {
        const vendorIds = scheduleData?.ddmLiveScheduleProductMappers?.map?.(({ productGroup }) => {
            return productGroup?.vendor?.id;
        });
        setSelectedVendor(new Set(vendorIds));

        const pids = new Set<string>();
        scheduleData?.ddmLiveScheduleProductMappers?.forEach((mapper) => {
            pids.add(mapper.productGroup.id);
        });
        setScheduledPidSet(pids);
    }, [scheduleData]);

    // 초기 데이터 로드 (카테고리, DimEntities) - 한 번만 실행
    useEffect(() => {
        if (isInitialDataLoaded) return; // 이미 로드되었으면 실행하지 않음

        const loadInitialData = async () => {
            try {
                // 카테고리와 DimEntities를 병렬로 로드
                const [categoriesResult, dimEntitiesResult] = await Promise.all([
                    fetchAllCategories(),
                    getDimEntities(),
                ]);

                setDimEntities(dimEntitiesResult);
                setIsInitialDataLoaded(true);
            } catch (error) {
                console.error('초기 데이터 로드 실패:', error);
                setIsInitialDataLoaded(true); // 에러 발생 시에도 플래그 설정하여 재시도 방지
            }
        };

        loadInitialData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 카테고리 이미지 매핑 함수
    const getCategoryImage = (gender: string, categoryName: string): string => {
        const categoryMap: { [key: string]: string } = {
            의류: 'clothing',
            신발: 'shoes',
            가방: 'bags',
            주얼리: 'jewelry',
            잡화: 'accessories',
            악세사리: 'accessories',
        };

        const categoryKey = categoryMap[categoryName] || 'clothing';
        const genderPrefix = gender === 'woman' ? 'women' : gender === 'men' ? 'men' : 'kids';
        const imageKey = `${genderPrefix}_${categoryKey}`;

        return categoryImageMap[imageKey] || womenClothing; // fallback to womenClothing
    };

    // 카테고리 데이터 통합 로드 (중복 호출 방지 - Zustand store 데이터 사용)
    useEffect(() => {
        // 초기 데이터가 로드되지 않았거나 이미 처리되었으면 실행하지 않음
        if (!isInitialDataLoaded || isCategoryDataProcessed) return;
        // store에 데이터가 없으면 실행하지 않음
        if (mainCategoriesFromStore.length === 0 || subCategoriesFromStore.length === 0) return;

        const loadAllCategoryData = async () => {
            try {
                setCategoryLoading(true);
                // Zustand store에서 이미 로드된 카테고리 데이터 사용 (중복 호출 방지)
                const mainCategories = mainCategoriesFromStore;
                const fashionCategory = mainCategories.find((cat) => cat.categoryName === '패션');

                if (fashionCategory) {
                    // Zustand store에서 이미 로드된 SUB 카테고리 데이터 사용 (중복 호출 방지)
                    const allCategories = subCategoriesFromStore;

                    // 패션 카테고리의 직접 하위 카테고리들 찾기 (여성, 남성, 유아동)
                    const genderCats = allCategories.filter(
                        (cat) =>
                            cat.parentCategoryId === fashionCategory.categoryId &&
                            ['여성', '남성', '유아동'].includes(cat.categoryName),
                    );

                    // 성별별 카테고리 ID 매핑
                    const newGenderCategoryIds = {
                        woman: genderCats.find((cat) => cat.categoryName === '여성')?.categoryId || null,
                        men: genderCats.find((cat) => cat.categoryName === '남성')?.categoryId || null,
                        kids: genderCats.find((cat) => cat.categoryName === '유아동')?.categoryId || null,
                    };

                    setGenderCategoryIds(newGenderCategoryIds);

                    // 드롭다운용 카테고리 데이터 생성
                    const newCategoryData: CategoryData = {};

                    // 각 성별 카테고리에 대해 처리
                    for (const genderCat of genderCats) {
                        const genderKey =
                            genderCat.categoryName === '여성'
                                ? 'woman'
                                : genderCat.categoryName === '남성'
                                  ? 'men'
                                  : 'kids';

                        // 해당 성별 카테고리의 직접 하위 카테고리들 찾기 (의류, 신발, 가방 등)
                        const secondLevelCategories = allCategories.filter(
                            (cat) => cat.parentCategoryId === genderCat.categoryId,
                        );

                        const categories = [];
                        for (const secondLevelCat of secondLevelCategories) {
                            // 해당 2단계 카테고리의 직접 하위 카테고리들 찾기 (상의, 니트, 팬츠 등)
                            const thirdLevelCategories = allCategories.filter(
                                (cat) => cat.parentCategoryId === secondLevelCat.categoryId,
                            );

                            const subcategoryNames = thirdLevelCategories.map((cat) => cat.categoryName);

                            categories.push({
                                name: secondLevelCat.categoryName,
                                icon: getCategoryImage(genderKey, secondLevelCat.categoryName),
                                subcategories: subcategoryNames,
                            });
                        }

                        newCategoryData[genderKey] = {
                            name: genderCat.categoryName,
                            categories: categories,
                        };
                    }

                    setCategoryData(newCategoryData);
                    setCategoriesReady(true); // 카테고리 로딩 완료
                    setIsCategoryDataProcessed(true); // 처리 완료 플래그 설정
                }
            } catch (error) {
                console.error('카테고리 데이터 로드 실패:', error);
                setCategoriesReady(true); // 에러 발생 시에도 로딩 완료 처리
                setIsCategoryDataProcessed(true); // 에러 발생 시에도 플래그 설정
            } finally {
                setCategoryLoading(false);
            }
        };

        loadAllCategoryData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInitialDataLoaded, isCategoryDataProcessed, mainCategoriesFromStore, subCategoriesFromStore]);

    useEffect(() => {
        if (!categoryDropdownOpen) return;

        const handleScroll = () => {
            setCategoryDropdownOpen(false);
        };

        window.addEventListener('scroll', handleScroll);

        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, [categoryDropdownOpen]);

    const handleCategoryButtonMouseEnter = () => {
        if (isMobile) return;
        setCategoryDropdownOpen(true);
    };

    const handleCategoryButtonMouseLeave = (event: React.MouseEvent<HTMLButtonElement>) => {
        if (isMobile) {
            setCategoryDropdownOpen(false);
            return;
        }

        const nextTarget = event.relatedTarget;
        if (nextTarget instanceof Node && categoryDropdownRef.current?.contains(nextTarget)) {
            return;
        }
        setCategoryDropdownOpen(false);
    };

    const handleCategoryDropdownMouseEnter = () => {
        if (isMobile) return;
        setCategoryDropdownOpen(true);
    };

    const handleCategoryDropdownMouseLeave = (event: React.MouseEvent<HTMLDivElement>) => {
        const nextTarget = event.relatedTarget;
        if (nextTarget instanceof Node && categoryButtonRef.current?.contains(nextTarget)) {
            return;
        }
        setCategoryDropdownOpen(false);
    };

    // 카테고리 데이터가 로드되거나 성별이 변경되면 파싱
    useEffect(() => {
        parseCategoryHierarchy();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [subCategoriesFromStore, selectedGender, genderCategoryIds]);

    // URL 파라미터에서 카테고리 정보 읽기
    useEffect(() => {
        const category = searchParams.get('category');
        const subcategory = searchParams.get('subcategory');
        const gender = searchParams.get('gender');

        // 성별 설정
        if (gender === 'woman' || gender === 'men' || gender === 'kids') {
            setSelectedGender(gender);
            // 성별이 변경되면 탭과 서브카테고리 초기화
            setSelectedTab(0);
            setCurrentSubCategories([]);
            setSelectedSubTab(0);
            setCurrentSubSubCategories([]);
            setSelectedSubSubTab(0);
        } else {
            setSelectedGender('woman'); // 기본값
        }

        if (category && subCategories.length > 0) {
            // 카테고리 이름으로 인덱스 찾기
            const categoryIndex = subCategories.findIndex((cat) => cat.categoryName === category);
            if (categoryIndex !== -1) {
                setSelectedTab(categoryIndex + 1);

                // 해당 카테고리의 하위 카테고리들 로드
                const clickedCategory = subCategories[categoryIndex];
                const subCategoriesOfClicked =
                    allCategories?.filter((cat) => cat.parentCategoryId === clickedCategory.categoryId) || [];
                setCurrentSubCategories(subCategoriesOfClicked);

                if (subcategory) {
                    // 서브카테고리 이름으로 인덱스 찾기
                    const subcategoryIndex = subCategoriesOfClicked.findIndex(
                        (cat) => cat.categoryName === subcategory,
                    );
                    if (subcategoryIndex !== -1) {
                        setSelectedSubTab(subcategoryIndex + 1);
                    } else {
                        setSelectedSubTab(0);
                    }
                } else {
                    setSelectedSubTab(0);
                }
            }
        }
    }, [searchParams, subCategories, allCategories]);

    // 선택된 카테고리 ID 추출 (4depth 지원)
    const selectedCategoryId = (() => {
        // 전체 선택된 경우 (메인 탭이 0인 경우)
        if (selectedTab === 0) {
            return getGenderCategoryId(selectedGender);
        }

        // 서브 카테고리 탭이 전체(0)인 경우
        if (selectedSubTab === 0) {
            return subCategories[selectedTab - 1]?.categoryId || getGenderCategoryId(selectedGender);
        }

        // 서브서브 카테고리 탭이 전체(0)인 경우
        if (selectedSubSubTab === 0 && currentSubSubCategories.length > 0) {
            return currentSubCategories[selectedSubTab - 1]?.categoryId;
        }

        // 4depth 카테고리가 선택된 경우
        if (selectedSubSubTab > 0 && currentSubSubCategories.length > 0) {
            return currentSubSubCategories[selectedSubSubTab - 1]?.categoryId;
        }

        // 3depth 카테고리가 선택된 경우
        if (selectedSubTab > 0 && currentSubCategories.length > 0) {
            return currentSubCategories[selectedSubTab - 1]?.categoryId;
        }

        // 메인 카테고리만 선택된 경우
        return subCategories[selectedTab - 1]?.categoryId || getGenderCategoryId(selectedGender);
    })();

    // 초기 데이터 로드
    useEffect(() => {
        const loadInitialData = async () => {
            try {
                setLoading(true);
                const paginationLimit = page >= 2 ? 40 : 20;

                // 건물 필터 계산 (여러 곳에서 사용)
                const buildingFilters: string[] = [];
                Object.entries(buildingFloorSelections).forEach(([building, floors]) => {
                    if (floors.length > 0) {
                        floors.forEach((floor) => {
                            if (floor === '전체') {
                                buildingFilters.push(building);
                            } else {
                                const convertedFloor = convertFloorToApiFormat(floor);
                                buildingFilters.push(`${building},${convertedFloor}`);
                            }
                        });
                    }
                });

                // 검색 키워드가 있으면 모든 필터를 s= 파라미터 안에 포함 (s=를 사용하면 filter=는 무시됨)
                if (appliedSearchKeywords.length > 0) {
                    // 검색 조건 생성 (각 키워드에 대해 OR 조건)
                    const searchConditions = appliedSearchKeywords.map((keyword) => ({
                        $or: [
                            { groupTitle: { $cont: keyword } },
                            { 'vendor.vendorBusinessInfo.storeName': { $cont: keyword } },
                            { 'vendor.nickname': { $cont: keyword } },
                            { 'vendor.vendorBusinessInfo.storeLocation': { $cont: keyword } },
                        ],
                    }));

                    // 여러 키워드가 있으면 $or로 묶기 (a이거나 b이거나...)
                    const searchQuery = searchConditions.length > 1 ? { $or: searchConditions } : searchConditions[0];

                    // 모든 필터 조건을 $and 배열로 구성
                    const andConditions: any[] = [searchQuery];

                    // 카테고리 필터
                    if (selectedCategoryId) {
                        andConditions.push({ 'category.id': { $eq: selectedCategoryId } });
                    }

                    // 제조국 필터
                    if (selectedCountries.length > 0) {
                        andConditions.push({ countryOfOrigin: { $in: selectedCountries } });
                    }

                    // 소재 필터
                    if (selectedMaterials.length > 0) {
                        andConditions.push({ materialInfo: { $in: selectedMaterials } });
                    }

                    // 사이즈 필터
                    const allSelectedSizes = [...selectedSizesAlpha, ...selectedSizesNumeric];
                    if (allSelectedSizes.length > 0) {
                        andConditions.push({ productInfo: { $in: allSelectedSizes } });
                    }

                    // 가격 필터
                    if (priceRange === 'under10k') {
                        andConditions.push({ 'representProduct.supplyPrice': { $lte: 10000 } });
                    } else if (priceRange === '10kTo30k') {
                        andConditions.push({ 'representProduct.supplyPrice': { $gte: 10000, $lte: 30000 } });
                    } else if (priceRange === 'over30k') {
                        andConditions.push({ 'representProduct.supplyPrice': { $gte: 30000 } });
                    } else if (priceRange === 'custom' && (customMinPrice || customMaxPrice)) {
                        const priceCondition: any = {};
                        if (customMinPrice) priceCondition.$gte = Number(customMinPrice);
                        if (customMaxPrice) priceCondition.$lte = Number(customMaxPrice);
                        andConditions.push({ 'representProduct.supplyPrice': priceCondition });
                    }

                    // 착용감 필터
                    if (selectedFits.length > 0) {
                        andConditions.push({ dimFit: { $in: selectedFits } });
                    }

                    // 패턴 필터
                    if (selectedDimPatterns.length > 0) {
                        andConditions.push({ dimPattern: { $in: selectedDimPatterns } });
                    }

                    // 계절감 필터
                    if (selectedSeasons.length > 0) {
                        andConditions.push({ dimSeason: { $in: selectedSeasons } });
                    }

                    // 비침 필터
                    if (selectedSheerness.length > 0) {
                        andConditions.push({ dimSheerness: { $in: selectedSheerness } });
                    }

                    // 신축성 필터
                    if (selectedStretch.length > 0) {
                        andConditions.push({ dimStretch: { $in: selectedStretch } });
                    }

                    // 두께감 필터
                    if (selectedDimThickness.length > 0) {
                        andConditions.push({ dimThickness: { $in: selectedDimThickness } });
                    }

                    // 스타일 필터
                    if (selectedDimStyles.length > 0) {
                        andConditions.push({ dimStyle: { $in: selectedDimStyles } });
                    }

                    // 건물 필터
                    if (buildingFilters.length > 0) {
                        const buildingFilterValue = buildingFilters.join(';');
                        andConditions.push({ storeLocation: { $in: [buildingFilterValue] } });
                    }

                    // 낱장가능 필터
                    if (singleSheetAvailable) {
                        andConditions.push({ singleUnitAvailable: { $eq: true } });
                    }

                    // 카테고리 필터가 없으면 API 호출하지 않음
                    if (!selectedCategoryId) {
                        setLoading(false);
                        return;
                    }

                    // $and 배열로 묶기
                    const sParamConditions = { $and: andConditions };

                    const queryStr = `s=${encodeURIComponent(JSON.stringify(sParamConditions))}`;

                    const data = await getProductListSimple({
                        page: 1,
                        limit: 20,
                        filter: queryStr,
                    });
                    setProducts(data.data);
                    setProductTotal(data.total);
                    setPage(1);
                    setHasMore(1 < data.pageCount);
                } else {
                    // 검색 키워드가 없으면 기존 방식대로 filter= 파라미터 사용
                    const qb = RequestQueryBuilder.create();

                    // 선택된 탭에 따른 카테고리 필터 설정
                    if (selectedCategoryId) {
                        qb.setFilter({
                            field: 'category.id',
                            operator: '$eq',
                            value: selectedCategoryId,
                        });
                    }

                    // 제조국 필터
                    if (selectedCountries.length > 0) {
                        qb.setFilter({
                            field: 'countryOfOrigin',
                            operator: '$in',
                            value: selectedCountries,
                        });
                    }

                    // 소재 필터
                    if (selectedMaterials.length > 0) {
                        qb.setFilter({
                            field: 'materialInfo',
                            operator: '$in',
                            value: selectedMaterials,
                        });
                    }

                    // 사이즈 필터
                    const allSelectedSizes = [...selectedSizesAlpha, ...selectedSizesNumeric];
                    if (allSelectedSizes.length > 0) {
                        qb.setFilter({
                            field: 'productInfo',
                            operator: '$in',
                            value: allSelectedSizes,
                        });
                    }

                    // 가격 필터
                    if (priceRange === 'under10k') {
                        qb.setFilter({
                            field: 'representProduct.supplyPrice',
                            operator: '$lte',
                            value: 10000,
                        });
                    } else if (priceRange === '10kTo30k') {
                        qb.setFilter({
                            field: 'representProduct.supplyPrice',
                            operator: '$gte',
                            value: 10000,
                        });
                        qb.setFilter({
                            field: 'representProduct.supplyPrice',
                            operator: '$lte',
                            value: 30000,
                        });
                    } else if (priceRange === 'over30k') {
                        qb.setFilter({
                            field: 'representProduct.supplyPrice',
                            operator: '$gte',
                            value: 30000,
                        });
                    } else if (priceRange === 'custom' && (customMinPrice || customMaxPrice)) {
                        if (customMinPrice) {
                            qb.setFilter({
                                field: 'representProduct.supplyPrice',
                                operator: '$gte',
                                value: Number(customMinPrice),
                            });
                        }
                        if (customMaxPrice) {
                            qb.setFilter({
                                field: 'representProduct.supplyPrice',
                                operator: '$lte',
                                value: Number(customMaxPrice),
                            });
                        }
                    }

                    // 착용감 필터
                    if (selectedFits.length > 0) {
                        qb.setFilter({
                            field: 'dimFit',
                            operator: '$in',
                            value: selectedFits,
                        });
                    }

                    // 패턴 필터
                    if (selectedDimPatterns.length > 0) {
                        qb.setFilter({
                            field: 'dimPattern',
                            operator: '$in',
                            value: selectedDimPatterns,
                        });
                    }

                    // 계절감 필터
                    if (selectedSeasons.length > 0) {
                        qb.setFilter({
                            field: 'dimSeason',
                            operator: '$in',
                            value: selectedSeasons,
                        });
                    }

                    // 비침 필터
                    if (selectedSheerness.length > 0) {
                        qb.setFilter({
                            field: 'dimSheerness',
                            operator: '$in',
                            value: selectedSheerness,
                        });
                    }

                    // 신축성 필터
                    if (selectedStretch.length > 0) {
                        qb.setFilter({
                            field: 'dimStretch',
                            operator: '$in',
                            value: selectedStretch,
                        });
                    }

                    // 두께감 필터
                    if (selectedDimThickness.length > 0) {
                        qb.setFilter({
                            field: 'dimThickness',
                            operator: '$in',
                            value: selectedDimThickness,
                        });
                    }

                    // 스타일 필터
                    if (selectedDimStyles.length > 0) {
                        qb.setFilter({
                            field: 'dimStyle',
                            operator: '$in',
                            value: selectedDimStyles,
                        });
                    }

                    // 건물 필터
                    if (buildingFilters.length > 0) {
                        const buildingFilterValue = buildingFilters.join(';');
                        qb.setFilter({
                            field: 'storeLocation',
                            operator: '$in',
                            value: [buildingFilterValue],
                        });
                    }

                    // 낱장가능 필터
                    if (singleSheetAvailable) {
                        qb.setFilter({
                            field: 'singleUnitAvailable',
                            operator: '$eq',
                            value: 't',
                        });
                    }

                    const queryStr = qb.query(false);

                    // 카테고리 필터가 없으면 API 호출하지 않음
                    if (!selectedCategoryId || !queryStr) {
                        setLoading(false);
                        return;
                    }

                    const data = await getProductListSimple({
                        page: 1,
                        limit: 20,
                        filter: queryStr,
                    });
                    setProducts(data.data);
                    setProductTotal(data.total);
                    setPage(1);
                    setHasMore(1 < data.pageCount);
                }
                // 필터 변경 시 스크롤을 맨 위로 즉시 이동
                window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
            } catch (error) {
                console.error('Failed to load products:', error);
            } finally {
                setLoading(false);
            }
        };

        // 카테고리 데이터가 준비되고, subCategories가 로드되었거나 전체 탭일 때만 실행
        if (categoriesReady && (subCategories.length > 0 || selectedTab === 0)) {
            loadInitialData();
            isInitialLoadRef.current = false; // 첫 로딩 완료 표시
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        categoriesReady,
        selectedTab,
        selectedSubTab,
        selectedSubSubTab,
        selectedCategoryId,
        selectedCountries.join(','),
        selectedMaterials.join(','),
        selectedSizesAlpha.join(','),
        selectedSizesNumeric.join(','),
        selectedDetails.join(','),
        selectedFits.join(','),
        selectedDimPatterns.join(','),
        selectedSeasons.join(','),
        selectedSheerness.join(','),
        selectedStretch.join(','),
        selectedDimStyles.join(','),
        selectedDimThickness.join(','),
        appliedSearchKeywords.join(','),
        JSON.stringify(buildingFloorSelections),
        singleSheetAvailable,
    ]);

    // 가격 범위 변경 시 API 호출 (직접입력 제외)
    useEffect(() => {
        // 첫 로딩 시에는 loadInitialData에서 처리하므로 건너뛰기
        if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
            return;
        }

        if (priceRange !== 'custom') {
            const loadPriceFilteredData = async () => {
                try {
                    setLoading(true);

                    // RequestQueryBuilder를 사용하여 쿼리 생성
                    const qb = RequestQueryBuilder.create();

                    // 선택된 탭에 따른 카테고리 필터 설정
                    if (selectedCategoryId) {
                        qb.setFilter({
                            field: 'category.id',
                            operator: '$eq',
                            value: selectedCategoryId,
                        });
                    }

                    // 검색 필터는 s= 파라미터로 별도 처리 (나중에 쿼리 문자열에 추가)

                    // 제조국 필터
                    if (selectedCountries.length > 0) {
                        qb.setFilter({
                            field: 'countryOfOrigin',
                            operator: '$in',
                            value: selectedCountries,
                        });
                    }

                    // 소재 필터
                    if (selectedMaterials.length > 0) {
                        qb.setFilter({
                            field: 'materialInfo',
                            operator: '$in',
                            value: selectedMaterials,
                        });
                    }

                    // 사이즈 필터
                    const allSelectedSizes = [...selectedSizesAlpha, ...selectedSizesNumeric];
                    if (allSelectedSizes.length > 0) {
                        qb.setFilter({
                            field: 'productInfo',
                            operator: '$in',
                            value: allSelectedSizes,
                        });
                    }

                    // 가격 필터
                    if (priceRange === 'all') {
                        // 전체는 가격 필터 없음
                    } else if (priceRange === 'under10k') {
                        qb.setFilter({
                            field: 'representProduct.supplyPrice',
                            operator: '$lte',
                            value: 10000,
                        });
                    } else if (priceRange === '10kTo30k') {
                        qb.setFilter({
                            field: 'representProduct.supplyPrice',
                            operator: '$gte',
                            value: 10000,
                        });
                        qb.setFilter({
                            field: 'representProduct.supplyPrice',
                            operator: '$lte',
                            value: 30000,
                        });
                    } else if (priceRange === 'over30k') {
                        qb.setFilter({
                            field: 'representProduct.supplyPrice',
                            operator: '$gte',
                            value: 30000,
                        });
                    }

                    // 새로 추가된 필터들
                    // 착용감 필터
                    if (selectedFits.length > 0) {
                        qb.setFilter({
                            field: 'dimFit',
                            operator: '$in',
                            value: selectedFits,
                        });
                    }

                    // 패턴 필터
                    if (selectedDimPatterns.length > 0) {
                        qb.setFilter({
                            field: 'dimPattern',
                            operator: '$in',
                            value: selectedDimPatterns,
                        });
                    }

                    // 계절감 필터
                    if (selectedSeasons.length > 0) {
                        qb.setFilter({
                            field: 'dimSeason',
                            operator: '$in',
                            value: selectedSeasons,
                        });
                    }

                    // 비침 필터
                    if (selectedSheerness.length > 0) {
                        qb.setFilter({
                            field: 'dimSheerness',
                            operator: '$in',
                            value: selectedSheerness,
                        });
                    }

                    // 신축성 필터
                    if (selectedStretch.length > 0) {
                        qb.setFilter({
                            field: 'dimStretch',
                            operator: '$in',
                            value: selectedStretch,
                        });
                    }

                    // 두께감 필터
                    if (selectedDimThickness.length > 0) {
                        qb.setFilter({
                            field: 'dimThickness',
                            operator: '$in',
                            value: selectedDimThickness,
                        });
                    }

                    // 스타일 필터
                    if (selectedDimStyles.length > 0) {
                        qb.setFilter({
                            field: 'dimStyle',
                            operator: '$in',
                            value: selectedDimStyles,
                        });
                    }

                    // 건물 필터
                    const buildingFilters: string[] = [];
                    Object.entries(buildingFloorSelections).forEach(([building, floors]) => {
                        if (floors.length > 0) {
                            floors.forEach((floor) => {
                                if (floor === '전체') {
                                    // 전체 선택 시 건물명만 추가
                                    buildingFilters.push(building);
                                } else {
                                    // 건물명,층수 형식으로 추가 (층수 변환 적용)
                                    const convertedFloor = convertFloorToApiFormat(floor);
                                    buildingFilters.push(`${building},${convertedFloor}`);
                                }
                            });
                        }
                    });
                    if (buildingFilters.length > 0) {
                        // 세미콜론으로 구분된 단일 문자열로 전달
                        const buildingFilterValue = buildingFilters.join(';');
                        qb.setFilter({
                            field: 'storeLocation',
                            operator: '$in',
                            value: [buildingFilterValue],
                        });
                    }

                    let queryStr = qb.query(false);

                    // 검색 필터를 s= 파라미터로 추가
                    if (appliedSearchKeywords.length > 0) {
                        // 각 키워드에 대해 OR 조건 생성 (groupTitle 또는 storeName에 포함)
                        const searchConditions = appliedSearchKeywords.map((keyword) => ({
                            $or: [
                                { groupTitle: { $cont: keyword } },
                                { 'vendor.vendorBusinessInfo.storeName': { $cont: keyword } },
                                { 'vendor.vendorBusinessInfo.storeLocation': { $cont: keyword } },
                            ],
                        }));

                        // 여러 키워드가 있으면 $and로 묶기
                        const searchQuery =
                            searchConditions.length > 1 ? { $and: searchConditions } : searchConditions[0];

                        const searchParam = `s=${encodeURIComponent(JSON.stringify(searchQuery))}`;
                        queryStr = queryStr ? `${queryStr}&${searchParam}` : searchParam;
                    }

                    // 카테고리 필터가 없으면 API 호출하지 않음
                    if (!selectedCategoryId || !queryStr) {
                        setLoading(false);
                        return;
                    }

                    const data = await getProductListSimple({
                        page: 1,
                        limit: 20,
                        filter: queryStr,
                    });
                    setProducts(data.data);
                    setProductTotal(data.total);
                    setPage(1);
                    setHasMore(1 < data.pageCount);
                    // 필터 변경 시 스크롤을 맨 위로 즉시 이동
                    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
                } catch (error) {
                    console.error('Failed to load products:', error);
                } finally {
                    setLoading(false);
                }
            };

            loadPriceFilteredData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [priceRange]);

    useEffect(() => {
        if (!hasMore) {
            return;
        }
        const target = loadMoreTriggerRef.current;
        if (!target) {
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                const isInitialPageEmpty = page === 1 && products.length === 0;

                if (entry.isIntersecting && !loading && !isInitialPageEmpty && !isFetchingNextPageRef.current) {
                    isFetchingNextPageRef.current = true;
                    setPage((prevPage) => prevPage + 1);
                }
            },
            {
                root: null,
                rootMargin: '0px 0px 3000px 0px',
                threshold: 0,
            },
        );

        observer.observe(target);

        return () => {
            observer.disconnect();
        };
    }, [hasMore, loading, page, products.length]);

    useEffect(() => {
        if (!loading) {
            isFetchingNextPageRef.current = false;
        }
    }, [loading]);

    useEffect(() => {
        if (!hasMore) {
            setIsBottomReached(false);
            return;
        }

        const target = loadMoreTriggerRef.current;
        if (!target) {
            setIsBottomReached(false);
            return;
        }

        const bottomObserver = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                setIsBottomReached(entry.isIntersecting);
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 0,
            },
        );

        bottomObserver.observe(target);

        return () => {
            bottomObserver.disconnect();
            setIsBottomReached(false);
        };
    }, [hasMore, products.length]);

    // 페이지 변경시 데이터 로드
    useEffect(() => {
        const loadMoreProducts = async () => {
            if (page === 1) return;

            try {
                setLoading(true);
                const paginationLimit = page >= 2 ? 40 : 20;

                // 건물 필터 계산 (여러 곳에서 사용)
                const buildingFilters: string[] = [];
                Object.entries(buildingFloorSelections).forEach(([building, floors]) => {
                    if (floors.length > 0) {
                        floors.forEach((floor) => {
                            if (floor === '전체') {
                                buildingFilters.push(building);
                            } else {
                                const convertedFloor = convertFloorToApiFormat(floor);
                                buildingFilters.push(`${building},${convertedFloor}`);
                            }
                        });
                    }
                });

                // 검색 키워드가 있으면 모든 필터를 s= 파라미터 안에 포함 (s=를 사용하면 filter=는 무시됨)
                if (appliedSearchKeywords.length > 0) {
                    // 검색 조건 생성 (각 키워드에 대해 OR 조건)
                    const searchConditions = appliedSearchKeywords.map((keyword) => ({
                        $or: [
                            { groupTitle: { $cont: keyword } },
                            { 'vendor.vendorBusinessInfo.storeName': { $cont: keyword } },
                            { 'vendor.nickname': { $cont: keyword } },
                            { 'vendor.vendorBusinessInfo.storeLocation': { $cont: keyword } },
                        ],
                    }));

                    // 여러 키워드가 있으면 $or로 묶기 (a이거나 b이거나...)
                    const searchQuery = searchConditions.length > 1 ? { $or: searchConditions } : searchConditions[0];

                    // 모든 필터 조건을 $and 배열로 구성
                    const andConditions: any[] = [searchQuery];

                    // 카테고리 필터
                    if (selectedCategoryId) {
                        andConditions.push({ 'category.id': { $eq: selectedCategoryId } });
                    }

                    // 제조국 필터
                    if (selectedCountries.length > 0) {
                        andConditions.push({ countryOfOrigin: { $in: selectedCountries } });
                    }

                    // 소재 필터
                    if (selectedMaterials.length > 0) {
                        andConditions.push({ materialInfo: { $in: selectedMaterials } });
                    }

                    // 사이즈 필터
                    const allSelectedSizes = [...selectedSizesAlpha, ...selectedSizesNumeric];
                    if (allSelectedSizes.length > 0) {
                        andConditions.push({ productInfo: { $in: allSelectedSizes } });
                    }

                    // 가격 필터
                    if (priceRange === 'under10k') {
                        andConditions.push({ 'representProduct.supplyPrice': { $lte: 10000 } });
                    } else if (priceRange === '10kTo30k') {
                        andConditions.push({ 'representProduct.supplyPrice': { $gte: 10000, $lte: 30000 } });
                    } else if (priceRange === 'over30k') {
                        andConditions.push({ 'representProduct.supplyPrice': { $gte: 30000 } });
                    } else if (priceRange === 'custom' && (customMinPrice || customMaxPrice)) {
                        const priceCondition: any = {};
                        if (customMinPrice) priceCondition.$gte = Number(customMinPrice);
                        if (customMaxPrice) priceCondition.$lte = Number(customMaxPrice);
                        andConditions.push({ 'representProduct.supplyPrice': priceCondition });
                    }

                    // 착용감 필터
                    if (selectedFits.length > 0) {
                        andConditions.push({ dimFit: { $in: selectedFits } });
                    }

                    // 패턴 필터
                    if (selectedDimPatterns.length > 0) {
                        andConditions.push({ dimPattern: { $in: selectedDimPatterns } });
                    }

                    // 계절감 필터
                    if (selectedSeasons.length > 0) {
                        andConditions.push({ dimSeason: { $in: selectedSeasons } });
                    }

                    // 비침 필터
                    if (selectedSheerness.length > 0) {
                        andConditions.push({ dimSheerness: { $in: selectedSheerness } });
                    }

                    // 신축성 필터
                    if (selectedStretch.length > 0) {
                        andConditions.push({ dimStretch: { $in: selectedStretch } });
                    }

                    // 두께감 필터
                    if (selectedDimThickness.length > 0) {
                        andConditions.push({ dimThickness: { $in: selectedDimThickness } });
                    }

                    // 스타일 필터
                    if (selectedDimStyles.length > 0) {
                        andConditions.push({ dimStyle: { $in: selectedDimStyles } });
                    }

                    // 건물 필터
                    if (buildingFilters.length > 0) {
                        const buildingFilterValue = buildingFilters.join(';');
                        andConditions.push({ storeLocation: { $in: [buildingFilterValue] } });
                    }

                    // 낱장가능 필터
                    if (singleSheetAvailable) {
                        andConditions.push({ singleUnitAvailable: { $eq: 't' } });
                    }

                    // $and 배열로 묶기
                    const sParamConditions = { $and: andConditions };

                    const queryStr = `s=${encodeURIComponent(JSON.stringify(sParamConditions))}`;

                    const newData = await getProductListSimple({
                        page,
                        limit: paginationLimit,
                        filter: queryStr,
                    });

                    setProducts((prev) => [...prev, ...newData.data]);
                    setHasMore(page < newData.pageCount);
                } else {
                    // 검색 키워드가 없으면 기존 방식대로 filter= 파라미터 사용
                    const qb = RequestQueryBuilder.create();

                    // 선택된 탭에 따른 카테고리 필터 설정
                    if (selectedCategoryId) {
                        qb.setFilter({
                            field: 'category.id',
                            operator: '$eq',
                            value: selectedCategoryId,
                        });
                    }

                    // 제조국 필터
                    if (selectedCountries.length > 0) {
                        qb.setFilter({
                            field: 'countryOfOrigin',
                            operator: '$in',
                            value: selectedCountries,
                        });
                    }

                    // 소재 필터
                    if (selectedMaterials.length > 0) {
                        qb.setFilter({
                            field: 'materialInfo',
                            operator: '$in',
                            value: selectedMaterials,
                        });
                    }

                    // 사이즈 필터
                    const allSelectedSizes = [...selectedSizesAlpha, ...selectedSizesNumeric];
                    if (allSelectedSizes.length > 0) {
                        qb.setFilter({
                            field: 'productInfo',
                            operator: '$in',
                            value: allSelectedSizes,
                        });
                    }

                    // 가격 필터
                    if (priceRange === 'under10k') {
                        qb.setFilter({
                            field: 'representProduct.supplyPrice',
                            operator: '$lte',
                            value: 10000,
                        });
                    } else if (priceRange === '10kTo30k') {
                        qb.setFilter({
                            field: 'representProduct.supplyPrice',
                            operator: '$gte',
                            value: 10000,
                        });
                        qb.setFilter({
                            field: 'representProduct.supplyPrice',
                            operator: '$lte',
                            value: 30000,
                        });
                    } else if (priceRange === 'over30k') {
                        qb.setFilter({
                            field: 'representProduct.supplyPrice',
                            operator: '$gte',
                            value: 30000,
                        });
                    } else if (priceRange === 'custom' && (customMinPrice || customMaxPrice)) {
                        if (customMinPrice) {
                            qb.setFilter({
                                field: 'representProduct.supplyPrice',
                                operator: '$gte',
                                value: Number(customMinPrice),
                            });
                        }
                        if (customMaxPrice) {
                            qb.setFilter({
                                field: 'representProduct.supplyPrice',
                                operator: '$lte',
                                value: Number(customMaxPrice),
                            });
                        }
                    }

                    // 착용감 필터
                    if (selectedFits.length > 0) {
                        qb.setFilter({
                            field: 'dimFit',
                            operator: '$in',
                            value: selectedFits,
                        });
                    }

                    // 패턴 필터
                    if (selectedDimPatterns.length > 0) {
                        qb.setFilter({
                            field: 'dimPattern',
                            operator: '$in',
                            value: selectedDimPatterns,
                        });
                    }

                    // 계절감 필터
                    if (selectedSeasons.length > 0) {
                        qb.setFilter({
                            field: 'dimSeason',
                            operator: '$in',
                            value: selectedSeasons,
                        });
                    }

                    // 비침 필터
                    if (selectedSheerness.length > 0) {
                        qb.setFilter({
                            field: 'dimSheerness',
                            operator: '$in',
                            value: selectedSheerness,
                        });
                    }

                    // 신축성 필터
                    if (selectedStretch.length > 0) {
                        qb.setFilter({
                            field: 'dimStretch',
                            operator: '$in',
                            value: selectedStretch,
                        });
                    }

                    // 두께감 필터
                    if (selectedDimThickness.length > 0) {
                        qb.setFilter({
                            field: 'dimThickness',
                            operator: '$in',
                            value: selectedDimThickness,
                        });
                    }

                    // 스타일 필터
                    if (selectedDimStyles.length > 0) {
                        qb.setFilter({
                            field: 'dimStyle',
                            operator: '$in',
                            value: selectedDimStyles,
                        });
                    }

                    // 건물 필터
                    if (buildingFilters.length > 0) {
                        const buildingFilterValue = buildingFilters.join(';');
                        qb.setFilter({
                            field: 'storeLocation',
                            operator: '$in',
                            value: [buildingFilterValue],
                        });
                    }

                    // 낱장가능 필터
                    if (singleSheetAvailable) {
                        qb.setFilter({
                            field: 'singleUnitAvailable',
                            operator: '$eq',
                            value: true,
                        });
                    }

                    const queryStr = qb.query(false);

                    const newData = await getProductListSimple({
                        page,
                        limit: paginationLimit,
                        filter: queryStr,
                    });

                    setProducts((prev) => [...prev, ...newData.data]);
                    setHasMore(page < newData.pageCount);
                }
            } catch (error) {
                console.error('Failed to load more products:', error);
            } finally {
                setLoading(false);
            }
        };

        loadMoreProducts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page]);

    // 상품 클릭 핸들러
    const handleOpen = async (originalProduct: Product) => {
        try {
            const updatedProduct = await getProductDetail({ productId: originalProduct.id });
            setSelectedProduct(updatedProduct);
            setOpen(true);
        } catch (error) {
            console.error('Failed to fetch product details:', error);
            setSelectedProduct(originalProduct);
            setOpen(true);
        }
    };

    // 모달 닫기 핸들러
    const handleClose = () => {
        setOpen(false);
        setSelectedProduct(null);
    };

    // 찜하기 토글
    const toggleFavorite = (productId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setFavorites((prev) => {
            const newFavorites = new Set(prev);
            if (newFavorites.has(productId)) {
                newFavorites.delete(productId);
            } else {
                newFavorites.add(productId);
            }
            return newFavorites;
        });
    };

    // 스케줄로 돌아가기 핸들러
    const handleBackToSchedule = () => {
        if (scheduleId && userId) {
            navigate(`/studio/${userId}/live/schedule-reg?scheduleId=${scheduleId}`);
        }
    };

    // 사이드바 휠 스크롤을 내부로 고정
    const handleSidebarWheel = (e: React.WheelEvent) => {
        e.stopPropagation();
        e.preventDefault();

        const el = sidebarRef.current;
        if (!el) return;

        // 휠 이벤트를 내부 스크롤로만 적용
        el.scrollTop += e.deltaY;
    };

    // 체크박스 핸들러들
    const handleMaterialChange = (material: string) => {
        setSelectedMaterials((prev) =>
            prev.includes(material) ? prev.filter((m) => m !== material) : [...prev, material],
        );
    };

    const handleColorChange = (color: string) => {
        setSelectedColors((prev) => (prev.includes(color) ? prev.filter((c) => c !== color) : [...prev, color]));
    };

    const handleCountryChange = (country: string) => {
        setSelectedCountries((prev) =>
            prev.includes(country) ? prev.filter((c) => c !== country) : [...prev, country],
        );
    };

    const handleSizeAlphaChange = (size: string) => {
        setSelectedSizesAlpha((prev) => (prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size]));
    };

    const handleSizeNumericChange = (size: string) => {
        setSelectedSizesNumeric((prev) => (prev.includes(size) ? prev.filter((s) => s !== size) : [...prev, size]));
    };

    const handleFitChange = (fit: string) => {
        setSelectedFits((prev) => (prev.includes(fit) ? prev.filter((f) => f !== fit) : [...prev, fit]));
    };

    const handleDimPatternChange = (pattern: string) => {
        setSelectedDimPatterns((prev) =>
            prev.includes(pattern) ? prev.filter((p) => p !== pattern) : [...prev, pattern],
        );
    };

    const handleSeasonChange = (season: string) => {
        setSelectedSeasons((prev) => (prev.includes(season) ? prev.filter((s) => s !== season) : [...prev, season]));
    };

    const handleSheernessChange = (sheerness: string) => {
        setSelectedSheerness((prev) =>
            prev.includes(sheerness) ? prev.filter((s) => s !== sheerness) : [...prev, sheerness],
        );
    };

    const handleStretchChange = (stretch: string) => {
        setSelectedStretch((prev) => (prev.includes(stretch) ? prev.filter((s) => s !== stretch) : [...prev, stretch]));
    };

    const handleDimStyleChange = (style: string) => {
        setSelectedDimStyles((prev) => (prev.includes(style) ? prev.filter((s) => s !== style) : [...prev, style]));
    };

    const handleDimThicknessChange = (thickness: string) => {
        setSelectedDimThickness((prev) =>
            prev.includes(thickness) ? prev.filter((t) => t !== thickness) : [...prev, thickness],
        );
    };

    // 건물 클릭 - 층 선택 UI 토글
    const handleBuildingClick = (building: string) => {
        if (expandedBuildingFloors === building) {
            setExpandedBuildingFloors(null);
        } else {
            setExpandedBuildingFloors(building);
        }
    };

    // 층 선택/해제
    const handleFloorSelect = (building: string, floor: string) => {
        setBuildingFloorSelections((prev) => {
            const currentFloors = prev[building] || [];

            // "전체" 선택 시
            if (floor === '전체') {
                if (currentFloors.includes('전체')) {
                    // 전체 해제
                    return { ...prev, [building]: [] };
                } else {
                    // 전체 선택
                    return { ...prev, [building]: ['전체'] };
                }
            }

            // 개별 층 선택 시
            if (currentFloors.includes(floor)) {
                // 해당 층 제거
                return { ...prev, [building]: currentFloors.filter((f) => f !== floor) };
            } else {
                // 해당 층 추가 (전체는 제거)
                return { ...prev, [building]: [...currentFloors.filter((f) => f !== '전체'), floor] };
            }
        });
    };

    // 건물 선택 해제 (x 버튼)
    const handleBuildingRemove = (building: string, e: React.MouseEvent) => {
        e.stopPropagation();
        setBuildingFloorSelections((prev) => {
            const newSelections = { ...prev };
            delete newSelections[building];
            return newSelections;
        });
        if (expandedBuildingFloors === building) {
            setExpandedBuildingFloors(null);
        }
    };

    // 가격 적용 핸들러
    const applyCustomPrice = async () => {
        if (customMinPrice || customMaxPrice) {
            setPriceRange('custom');

            // 직접 API 호출
            try {
                setLoading(true);

                // RequestQueryBuilder를 사용하여 쿼리 생성
                const qb = RequestQueryBuilder.create();

                // 선택된 탭에 따른 카테고리 필터 설정
                if (selectedCategoryId) {
                    qb.setFilter({
                        field: 'category.id',
                        operator: '$eq',
                        value: selectedCategoryId,
                    });
                }

                // 검색 필터는 s= 파라미터로 별도 처리 (나중에 쿼리 문자열에 추가)

                // 제조국 필터
                if (selectedCountries.length > 0) {
                    qb.setFilter({
                        field: 'countryOfOrigin',
                        operator: '$in',
                        value: selectedCountries,
                    });
                }

                // 소재 필터
                if (selectedMaterials.length > 0) {
                    qb.setFilter({
                        field: 'materialInfo',
                        operator: '$in',
                        value: selectedMaterials,
                    });
                }

                // 사이즈 필터
                const allSelectedSizes = [...selectedSizesAlpha, ...selectedSizesNumeric];
                if (allSelectedSizes.length > 0) {
                    qb.setFilter({
                        field: 'productInfo',
                        operator: '$in',
                        value: allSelectedSizes,
                    });
                }

                // 커스텀 가격 필터
                if (customMinPrice) {
                    qb.setFilter({
                        field: 'representProduct.supplyPrice',
                        operator: '$gte',
                        value: Number(customMinPrice),
                    });
                }
                if (customMaxPrice) {
                    qb.setFilter({
                        field: 'representProduct.supplyPrice',
                        operator: '$lte',
                        value: Number(customMaxPrice),
                    });
                }

                // 새로 추가된 필터들
                // 착용감 필터
                if (selectedFits.length > 0) {
                    qb.setFilter({
                        field: 'dimFit',
                        operator: '$in',
                        value: selectedFits,
                    });
                }

                // 패턴 필터
                if (selectedDimPatterns.length > 0) {
                    qb.setFilter({
                        field: 'dimPattern',
                        operator: '$in',
                        value: selectedDimPatterns,
                    });
                }

                // 계절감 필터
                if (selectedSeasons.length > 0) {
                    qb.setFilter({
                        field: 'dimSeason',
                        operator: '$in',
                        value: selectedSeasons,
                    });
                }

                // 비침 필터
                if (selectedSheerness.length > 0) {
                    qb.setFilter({
                        field: 'dimSheerness',
                        operator: '$in',
                        value: selectedSheerness,
                    });
                }

                // 신축성 필터
                if (selectedStretch.length > 0) {
                    qb.setFilter({
                        field: 'dimStretch',
                        operator: '$in',
                        value: selectedStretch,
                    });
                }

                // 두께감 필터
                if (selectedDimThickness.length > 0) {
                    qb.setFilter({
                        field: 'dimThickness',
                        operator: '$in',
                        value: selectedDimThickness,
                    });
                }

                // 스타일 필터
                if (selectedDimStyles.length > 0) {
                    qb.setFilter({
                        field: 'dimStyle',
                        operator: '$in',
                        value: selectedDimStyles,
                    });
                }

                // 건물 필터
                const buildingFilters: string[] = [];
                Object.entries(buildingFloorSelections).forEach(([building, floors]) => {
                    if (floors.length > 0) {
                        floors.forEach((floor) => {
                            if (floor === '전체') {
                                // 전체 선택 시 건물명만 추가
                                buildingFilters.push(building);
                            } else {
                                // 건물명,층수 형식으로 추가 (층수 변환 적용)
                                const convertedFloor = convertFloorToApiFormat(floor);
                                buildingFilters.push(`${building},${convertedFloor}`);
                            }
                        });
                    }
                });
                if (buildingFilters.length > 0) {
                    // 세미콜론으로 구분된 단일 문자열로 전달
                    const buildingFilterValue = buildingFilters.join(';');
                    qb.setFilter({
                        field: 'storeLocation',
                        operator: '$in',
                        value: [buildingFilterValue],
                    });
                }

                let queryStr = qb.query(false);

                // 검색 필터를 s= 파라미터로 추가
                if (appliedSearchKeywords.length > 0) {
                    // 각 키워드에 대해 OR 조건 생성 (groupTitle 또는 storeName 또는 nickname에 포함)
                    const searchConditions = appliedSearchKeywords.map((keyword) => ({
                        $or: [
                            { groupTitle: { $cont: keyword } },
                            { 'vendor.vendorBusinessInfo.storeName': { $cont: keyword } },
                            { 'vendor.nickname': { $cont: keyword } },
                        ],
                    }));

                    // 여러 키워드가 있으면 $or로 묶기 (a이거나 b이거나...)
                    const searchQuery = searchConditions.length > 1 ? { $or: searchConditions } : searchConditions[0];

                    const searchParam = `s=${encodeURIComponent(JSON.stringify(searchQuery))}`;
                    queryStr = queryStr ? `${queryStr}&${searchParam}` : searchParam;
                }

                const data = await getProductListSimple({
                    page: 1,
                    limit: 20,
                    filter: queryStr,
                });
                setProducts(data.data);
                setProductTotal(data.total);
                setPage(1);
                setHasMore(1 < data.pageCount);
                // 필터 변경 시 스크롤을 맨 위로 즉시 이동
                window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
            } catch (error) {
                console.error('Failed to load products:', error);
            } finally {
                setLoading(false);
            }
        }
    };

    // 왼쪽 사이드바 렌더링
    const renderSidebar = () => (
        <Box
            sx={{
                width: 220,
                minWidth: 220,
                flexShrink: 0,
                mr: 3,
                display: { xs: 'none', md: 'block' },
                position: 'sticky',
                top: '80px', // 네비게이션 바 높이 + 여백 고려
                maxHeight: 'calc(85vh - 96px)', // 화면 높이 - top 값 - 하단 여백
                overflowY: 'auto',
                overscrollBehavior: 'contain',
                // 스크롤바를 호버/스크롤 시에만 표시
                '&::-webkit-scrollbar': {
                    width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                    background: 'transparent',
                    borderRadius: '3px',
                    transition: 'background 0.2s',
                },
                '&:hover::-webkit-scrollbar-thumb': {
                    background: '#ccc',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    background: '#999',
                },
                scrollbarWidth: 'thin',
                scrollbarColor: 'transparent transparent',
                '&:hover': {
                    scrollbarColor: '#ccc transparent',
                },
            }}
            ref={sidebarRef}
            onWheel={handleSidebarWheel}
        >
            {/* 검색 필터 */}
            <Box sx={{ mb: 2, borderBottom: '1px solid #eee', pb: 2 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: '#222', mb: 1 }}>
                    {t('catalog.search')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                    <TextField
                        size="small"
                        placeholder={t('catalog.enterSearchKeyword')}
                        inputRef={searchKeywordRef}
                        onKeyPress={(e) => {
                            const keyword = searchKeywordRef.current?.value.trim();
                            if (e.key === 'Enter' && keyword && !appliedSearchKeywords.includes(keyword)) {
                                setAppliedSearchKeywords([...appliedSearchKeywords, keyword]);
                                if (searchKeywordRef.current) {
                                    searchKeywordRef.current.value = '';
                                }
                            }
                        }}
                        sx={{
                            flex: 1,
                            '& .MuiOutlinedInput-root': {
                                height: 32,
                                fontSize: 13,
                            },
                        }}
                    />
                    <IconButton
                        size="small"
                        onClick={() => {
                            const keyword = searchKeywordRef.current?.value.trim();
                            if (keyword && !appliedSearchKeywords.includes(keyword)) {
                                setAppliedSearchKeywords([...appliedSearchKeywords, keyword]);
                                if (searchKeywordRef.current) {
                                    searchKeywordRef.current.value = '';
                                }
                            }
                        }}
                        sx={{
                            width: 32,
                            height: 32,
                            border: '1px solid #ddd',
                            borderRadius: 1,
                            color: '#666',
                            mt: 1,
                            '&:hover': {
                                borderColor: '#999',
                                backgroundColor: '#f5f5f5',
                            },
                        }}
                    >
                        <SearchIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                </Box>
            </Box>

            {/* 제조국 섹션 */}
            <Box sx={{ mb: 2, borderBottom: '1px solid #eee', pb: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1.5,
                        cursor: 'pointer',
                    }}
                    onClick={() => setSidebarExpanded((prev) => ({ ...prev, country: !prev.country }))}
                >
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#222' }}>
                        {t('catalog.manufacturingCountry')}
                    </Typography>
                    {sidebarExpanded.country ? (
                        <ExpandLess sx={{ fontSize: 18, color: '#666' }} />
                    ) : (
                        <ExpandMore sx={{ fontSize: 18, color: '#666' }} />
                    )}
                </Box>
                <Collapse in={sidebarExpanded.country}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                        }}
                    >
                        {(showAllCountries ? COUNTRIES : COUNTRIES.slice(0, 20)).map((country) => (
                            <Button
                                key={country}
                                variant="outlined"
                                size="small"
                                onClick={() => handleCountryChange(country)}
                                sx={{
                                    minWidth: 'auto',
                                    px: 1.2,
                                    py: 0.4,
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: '#222',
                                    textTransform: 'none',
                                    borderColor: selectedCountries.includes(country) ? '#222' : '#ddd',
                                    backgroundColor: selectedCountries.includes(country) ? '#f5f5f5' : '#fff',
                                    '&:hover': {
                                        borderColor: '#222',
                                        backgroundColor: '#f5f5f5',
                                    },
                                }}
                            >
                                {t(`catalog.countries.${country}`)}
                            </Button>
                        ))}
                    </Box>
                    {COUNTRIES.length > 20 && (
                        <Typography
                            onClick={() => setShowAllCountries(!showAllCountries)}
                            sx={{
                                fontSize: 11,
                                color: '#999',
                                textAlign: 'center',
                                mt: 0.5,
                                cursor: 'pointer',
                                textDecoration: 'underline',
                            }}
                        >
                            {showAllCountries ? t('common.collapse') : t('common.more')}
                        </Typography>
                    )}
                </Collapse>
            </Box>
            <Box sx={{ mb: 2, borderBottom: '1px solid #eee', pb: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1.5,
                        cursor: 'pointer',
                    }}
                    onClick={() => setSidebarExpanded((prev) => ({ ...prev, building: !prev.building }))}
                >
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#222' }}>
                        {t('catalog.building')}
                    </Typography>
                    {sidebarExpanded.building ? (
                        <ExpandLess sx={{ fontSize: 18, color: '#666' }} />
                    ) : (
                        <ExpandMore sx={{ fontSize: 18, color: '#666' }} />
                    )}
                </Box>
                <Collapse in={sidebarExpanded.building}>
                    {/* 지역 탭 */}
                    <Tabs
                        value={selectedBuildingTab}
                        onChange={(_: React.SyntheticEvent, v: number) => setSelectedBuildingTab(v)}
                        sx={{
                            minHeight: 48,
                            mb: 1.5,
                            borderBottom: '1px solid #eee',
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#000',
                                height: 2,
                            },
                            '& .MuiTab-root': {
                                fontSize: 13,
                                fontWeight: 500,
                                color: '#999',
                                minHeight: 48,
                                py: 1,
                                px: 1,
                                minWidth: 0,
                                flex: 1,
                                textTransform: 'none',
                                whiteSpace: 'normal',
                                lineHeight: 1.3,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                '& .MuiTab-label': {
                                    whiteSpace: 'normal',
                                    lineHeight: 1.3,
                                    textAlign: 'center',
                                },
                                '&.Mui-selected': {
                                    color: '#000',
                                    fontWeight: 700,
                                },
                            },
                        }}
                    >
                        <Tab
                            label={
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        lineHeight: 1.2,
                                        fontSize: 13,
                                        fontWeight: 'inherit',
                                    }}
                                >
                                    {t('catalog.dongdaemun').includes('Dongdaemun') ? (
                                        <>
                                            <Box>Dong</Box>
                                            <Box>daemun</Box>
                                        </>
                                    ) : t('catalog.dongdaemun').includes('Namdaemun') ? (
                                        <>
                                            <Box>Nam</Box>
                                            <Box>daemun</Box>
                                        </>
                                    ) : (
                                        t('catalog.dongdaemun')
                                    )}
                                </Box>
                            }
                        />
                        <Tab
                            label={
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        lineHeight: 1.2,
                                        fontSize: 13,
                                        fontWeight: 'inherit',
                                    }}
                                >
                                    {t('catalog.namdaemun').includes('Namdaemun') ? (
                                        <>
                                            <Box>Nam</Box>
                                            <Box>daemun</Box>
                                        </>
                                    ) : (
                                        t('catalog.namdaemun')
                                    )}
                                </Box>
                            }
                        />
                        <Tab label={t('catalog.busan')} />
                    </Tabs>

                    {/* 정렬 옵션 */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 1.5, justifyContent: 'flex-end' }}>
                        <Typography
                            onClick={() => setBuildingSortOrder('recommended')}
                            sx={{
                                fontSize: 12,
                                fontWeight: buildingSortOrder === 'recommended' ? 700 : 400,
                                color: buildingSortOrder === 'recommended' ? '#000' : '#999',
                                cursor: 'pointer',
                                textDecoration: buildingSortOrder === 'recommended' ? 'underline' : 'none',
                            }}
                        >
                            {t('catalog.recommended')}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: '#ddd' }}>|</Typography>
                        <Typography
                            onClick={() => setBuildingSortOrder('alphabetical')}
                            sx={{
                                fontSize: 12,
                                fontWeight: buildingSortOrder === 'alphabetical' ? 700 : 400,
                                color: buildingSortOrder === 'alphabetical' ? '#000' : '#999',
                                cursor: 'pointer',
                                textDecoration: buildingSortOrder === 'alphabetical' ? 'underline' : 'none',
                            }}
                        >
                            {t('catalog.alphabetical')}
                        </Typography>
                    </Box>

                    {/* 건물 리스트 */}
                    <Box>
                        {(() => {
                            const regions = Object.keys(BUILDINGS) as Array<keyof typeof BUILDINGS>;
                            const currentRegion = regions[selectedBuildingTab];
                            let buildings = BUILDINGS[currentRegion];

                            // 지역별 층 정보 매핑
                            const floorDataMap = [DDM_BUILDING_FLOORS, NMD_BUILDING_FLOORS, BUSAN_BUILDING_FLOORS];
                            const currentFloorData = floorDataMap[selectedBuildingTab];

                            // 정렬
                            if (buildingSortOrder === 'alphabetical') {
                                buildings = [...buildings].sort((a, b) => a.localeCompare(b, 'ko'));
                            }

                            const displayBuildings = showAllBuildings ? buildings : buildings.slice(0, 10);

                            return (
                                <>
                                    {displayBuildings.map((building) => {
                                        const isSelected = buildingFloorSelections[building]?.length > 0;
                                        const isExpanded = expandedBuildingFloors === building;
                                        const selectedFloors = buildingFloorSelections[building] || [];
                                        const availableFloors = currentFloorData[building] || ['전체', '1층'];

                                        return (
                                            <Box key={building} sx={{ mb: 1 }}>
                                                {/* 건물명 */}
                                                <Box
                                                    onClick={() => handleBuildingClick(building)}
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        py: 0.5,
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            backgroundColor: '#f9f9f9',
                                                        },
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: 14,
                                                            color: isSelected ? theme.palette.primary.main : '#222',
                                                            fontWeight: isSelected ? 600 : 400,
                                                            flex: 1,
                                                        }}
                                                    >
                                                        {getBuildingName(building, currentRegion)}
                                                    </Typography>
                                                    {isSelected && (
                                                        <IconButton
                                                            onClick={(e: React.MouseEvent) =>
                                                                handleBuildingRemove(building, e)
                                                            }
                                                            sx={{
                                                                width: 20,
                                                                height: 20,
                                                                p: 0,
                                                                ml: 1,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    width: 16,
                                                                    height: 16,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: '#ddd',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    fontSize: 10,
                                                                    color: '#fff',
                                                                    fontWeight: 700,
                                                                }}
                                                            >
                                                                ×
                                                            </Box>
                                                        </IconButton>
                                                    )}
                                                </Box>

                                                {/* 층 선택 UI */}
                                                <Collapse in={isExpanded}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexWrap: 'wrap',
                                                            gap: 0.5,
                                                            mt: 0.5,
                                                            pl: 0.5,
                                                        }}
                                                    >
                                                        {availableFloors.map((floor) => (
                                                            <Button
                                                                key={floor}
                                                                variant="outlined"
                                                                size="small"
                                                                onClick={() => handleFloorSelect(building, floor)}
                                                                sx={{
                                                                    minWidth: 'auto',
                                                                    px: 1.2,
                                                                    py: 0.3,
                                                                    fontSize: 13,
                                                                    fontWeight: 400,
                                                                    color: selectedFloors.includes(floor)
                                                                        ? '#000'
                                                                        : '#666',
                                                                    borderColor: selectedFloors.includes(floor)
                                                                        ? '#000'
                                                                        : '#ddd',
                                                                    backgroundColor: selectedFloors.includes(floor)
                                                                        ? '#f5f5f5'
                                                                        : '#fff',
                                                                    borderRadius: '20px',
                                                                    '&:hover': {
                                                                        borderColor: '#000',
                                                                        backgroundColor: '#f5f5f5',
                                                                    },
                                                                }}
                                                            >
                                                                {floor === '전체' ? t('common.all') : floor}
                                                            </Button>
                                                        ))}
                                                    </Box>
                                                </Collapse>
                                            </Box>
                                        );
                                    })}
                                    {buildings.length > 10 && (
                                        <Box
                                            onClick={() => setShowAllBuildings(!showAllBuildings)}
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                py: 1,
                                                cursor: 'pointer',
                                                mt: 0.5,
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: 11,
                                                    color: '#999',
                                                    textDecoration: 'underline',
                                                }}
                                            >
                                                {showAllBuildings ? t('common.collapse') : t('common.more')}
                                            </Typography>
                                            {showAllBuildings ? (
                                                <ExpandLess sx={{ fontSize: 16, color: '#999', ml: 0.3 }} />
                                            ) : (
                                                <ExpandMore sx={{ fontSize: 16, color: '#999', ml: 0.3 }} />
                                            )}
                                        </Box>
                                    )}
                                </>
                            );
                        })()}
                    </Box>
                </Collapse>
            </Box>
            {/* 색상 섹션 */}
            {/* <Box sx={{ mb: 2, borderBottom: '1px solid #eee', pb: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1.5,
                        cursor: 'pointer',
                    }}
                    onClick={() => setSidebarExpanded((prev) => ({ ...prev, color: !prev.color }))}
                >
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#222' }}>{t('catalog.color')}</Typography>
                    {sidebarExpanded.color ? (
                        <ExpandLess sx={{ fontSize: 18, color: '#666' }} />
                    ) : (
                        <ExpandMore sx={{ fontSize: 18, color: '#666' }} />
                    )}
                </Box>
                <Collapse in={sidebarExpanded.color}>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gap: 0.5,
                        }}
                    >
                        {(showAllColors ? COLOR_PALETTE : COLOR_PALETTE.slice(0, 12)).map((color) => (
                            <Box
                                key={color.name}
                                onClick={() => handleColorChange(color.name)}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    p: 1,
                                    cursor: 'pointer',
                                    borderRadius: 1,
                                    backgroundColor: selectedColors.includes(color.name) ? '#f5f5f5' : 'transparent',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        backgroundColor: '#f9f9f9',
                                    },
                                }}
                            >
                                <Box
                                    sx={{
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        background: color.value,
                                        border: '1px solid #ddd',
                                        mb: 0.5,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        position: 'relative',
                                        ...(selectedColors.includes(color.name) && {
                                            border: '2px solid #222',
                                            '&::after': {
                                                content: '"✓"',
                                                color: '#fff',
                                                fontSize: 12,
                                                fontWeight: 'bold',
                                                textShadow: '0 0 2px rgba(0,0,0,0.8)',
                                            },
                                        }),
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: 10,
                                        color: '#666',
                                        textAlign: 'center',
                                        fontWeight: selectedColors.includes(color.name) ? 600 : 400,
                                    }}
                                >
                                    {color.name}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    {COLOR_PALETTE.length > 12 && (
                        <Typography
                            onClick={() => setShowAllColors(!showAllColors)}
                            sx={{
                                fontSize: 11,
                                color: '#999',
                                textAlign: 'center',
                                mt: 0.5,
                                cursor: 'pointer',
                                textDecoration: 'underline',
                            }}
                        >
                            {showAllColors ? t('common.collapse') : t('common.more')}
                        </Typography>
                    )}
                </Collapse>
            </Box> */}
            {/* 가격 섹션 */}
            <Box sx={{ mb: 2, borderBottom: '1px solid #eee', pb: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1.5,
                        cursor: 'pointer',
                    }}
                    onClick={() => setSidebarExpanded((prev) => ({ ...prev, price: !prev.price }))}
                >
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#222' }}>{t('catalog.price')}</Typography>
                    {sidebarExpanded.price ? (
                        <ExpandLess sx={{ fontSize: 18, color: '#666' }} />
                    ) : (
                        <ExpandMore sx={{ fontSize: 18, color: '#666' }} />
                    )}
                </Box>
                <Collapse in={sidebarExpanded.price}>
                    <RadioGroup value={priceRange} onChange={(e) => setPriceRange(e.target.value)}>
                        <FormControlLabel
                            value="all"
                            control={<Radio size="small" sx={{ py: 0.3 }} />}
                            label={t('common.all')}
                            sx={{
                                m: 0,
                                mb: 0.3,
                                '& .MuiFormControlLabel-label': {
                                    fontSize: 14,
                                    color: '#222',
                                    fontWeight: 400,
                                },
                            }}
                        />
                        <FormControlLabel
                            value="under10k"
                            control={<Radio size="small" sx={{ py: 0.3 }} />}
                            label={t('catalog.priceUnder10k')}
                            sx={{
                                m: 0,
                                mb: 0.3,
                                '& .MuiFormControlLabel-label': {
                                    fontSize: 14,
                                    color: '#222',
                                    fontWeight: 400,
                                },
                            }}
                        />
                        <FormControlLabel
                            value="10kTo30k"
                            control={<Radio size="small" sx={{ py: 0.3 }} />}
                            label={t('catalog.price10kTo30k')}
                            sx={{
                                m: 0,
                                mb: 0.3,
                                '& .MuiFormControlLabel-label': {
                                    fontSize: 14,
                                    color: '#222',
                                    fontWeight: 400,
                                },
                            }}
                        />
                        <FormControlLabel
                            value="over30k"
                            control={<Radio size="small" sx={{ py: 0.3 }} />}
                            label={t('catalog.priceOver30k')}
                            sx={{
                                m: 0,
                                mb: 0.3,
                                '& .MuiFormControlLabel-label': {
                                    fontSize: 14,
                                    color: '#222',
                                    fontWeight: 400,
                                },
                            }}
                        />
                        <FormControlLabel
                            value="custom"
                            control={<Radio size="small" sx={{ py: 0.3 }} />}
                            label={t('catalog.directInput')}
                            sx={{
                                m: 0,
                                mb: 0.5,
                                '& .MuiFormControlLabel-label': {
                                    fontSize: 14,
                                    color: '#222',
                                    fontWeight: 400,
                                },
                            }}
                        />
                    </RadioGroup>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                        <TextField
                            size="small"
                            placeholder="0"
                            value={customMinPrice}
                            onChange={(e) => setCustomMinPrice(e.target.value)}
                            type="number"
                            disabled={priceRange !== 'custom'}
                            sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                    height: 28,
                                    fontSize: 11,
                                    backgroundColor: priceRange !== 'custom' ? '#f5f5f5' : '#fff',
                                    '& input': {
                                        padding: '4px 8px',
                                    },
                                },
                            }}
                        />
                        <Typography sx={{ fontSize: 11, color: '#666' }}>~</Typography>
                        <TextField
                            size="small"
                            placeholder="0"
                            value={customMaxPrice}
                            onChange={(e) => setCustomMaxPrice(e.target.value)}
                            type="number"
                            disabled={priceRange !== 'custom'}
                            sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                    height: 28,
                                    fontSize: 11,
                                    backgroundColor: priceRange !== 'custom' ? '#f5f5f5' : '#fff',
                                    '& input': {
                                        padding: '4px 8px',
                                    },
                                },
                            }}
                        />
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={priceRange !== 'custom'}
                            onClick={applyCustomPrice}
                            sx={{
                                minWidth: 40,
                                height: 28,
                                fontSize: 11,
                                px: 1,
                                borderColor: '#ddd',
                                color: '#666',
                                textTransform: 'none',
                                mt: 1,
                                alignSelf: 'stretch',
                                '&:hover': {
                                    borderColor: '#999',
                                    backgroundColor: 'transparent',
                                },
                                '&.Mui-disabled': {
                                    color: '#999',
                                    borderColor: '#eee',
                                },
                            }}
                        >
                            {t('common.apply')}
                        </Button>
                    </Box>
                </Collapse>
            </Box>
            {/* 건물 섹션 */}

            {/* 사이즈 섹션 */}
            <Box sx={{ mb: 2, borderBottom: '1px solid #eee', pb: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1.5,
                        cursor: 'pointer',
                    }}
                    onClick={() => setSidebarExpanded((prev) => ({ ...prev, size: !prev.size }))}
                >
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#222' }}>{t('catalog.size')}</Typography>
                    {sidebarExpanded.size ? (
                        <ExpandLess sx={{ fontSize: 18, color: '#666' }} />
                    ) : (
                        <ExpandMore sx={{ fontSize: 18, color: '#666' }} />
                    )}
                </Box>
                <Collapse in={sidebarExpanded.size}>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {SIZES_ALPHA.map((size) => (
                            <Button
                                key={size}
                                variant="outlined"
                                size="small"
                                onClick={() => handleSizeAlphaChange(size)}
                                sx={{
                                    minWidth: 'auto',
                                    px: 1.5,
                                    py: 0.4,
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: '#222',
                                    textTransform: 'none',
                                    borderColor: selectedSizesAlpha.includes(size) ? '#222' : '#ddd',
                                    backgroundColor: selectedSizesAlpha.includes(size) ? '#f5f5f5' : '#fff',
                                    '&:hover': {
                                        borderColor: '#222',
                                        backgroundColor: '#f5f5f5',
                                    },
                                }}
                            >
                                {t(`catalog.sizes.${size}`)}
                            </Button>
                        ))}
                        {SIZES_NUMERIC.map((size) => (
                            <Button
                                key={size}
                                variant="outlined"
                                size="small"
                                onClick={() => handleSizeNumericChange(size)}
                                sx={{
                                    minWidth: 'auto',
                                    px: 1.2,
                                    py: 0.4,
                                    fontSize: 14,
                                    fontWeight: 500,
                                    color: '#222',
                                    textTransform: 'none',
                                    borderColor: selectedSizesNumeric.includes(size) ? '#222' : '#ddd',
                                    backgroundColor: selectedSizesNumeric.includes(size) ? '#f5f5f5' : '#fff',
                                    '&:hover': {
                                        borderColor: '#222',
                                        backgroundColor: '#f5f5f5',
                                    },
                                }}
                            >
                                {t(`catalog.sizes.${size}`)}
                            </Button>
                        ))}
                    </Box>
                </Collapse>
            </Box>

            {/* 상세정보 섹션 */}
            <Box sx={{ mb: 2, borderBottom: '1px solid #eee', pb: 2 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1.5,
                        cursor: 'pointer',
                    }}
                    onClick={() => setSidebarExpanded((prev) => ({ ...prev, detail: !prev.detail }))}
                >
                    <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#222' }}>
                        {t('catalog.details')}
                    </Typography>
                    {sidebarExpanded.detail ? (
                        <ExpandLess sx={{ fontSize: 18, color: '#666' }} />
                    ) : (
                        <ExpandMore sx={{ fontSize: 18, color: '#666' }} />
                    )}
                </Box>
                <Collapse in={sidebarExpanded.detail}>
                    {/* 소재 하위 섹션 */}
                    <Box sx={{ mb: 2 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#333', mb: 1.5 }}>
                            {t('catalog.material')}
                        </Typography>
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 0.5,
                            }}
                        >
                            {(showAllMaterials ? MATERIAL_OPTIONS : MATERIAL_OPTIONS.slice(0, 15)).map((material) => (
                                <Button
                                    key={material.value}
                                    variant="outlined"
                                    size="small"
                                    onClick={() => handleMaterialChange(material.value)}
                                    sx={{
                                        minWidth: 'auto',
                                        px: 1.2,
                                        py: 0.4,
                                        fontSize: 14,
                                        fontWeight: 500,
                                        color: '#222',
                                        textTransform: 'none',
                                        borderColor: selectedMaterials.includes(material.value) ? '#222' : '#ddd',
                                        backgroundColor: selectedMaterials.includes(material.value)
                                            ? '#f5f5f5'
                                            : '#fff',
                                        '&:hover': {
                                            borderColor: '#222',
                                            backgroundColor: '#f5f5f5',
                                        },
                                    }}
                                >
                                    {t(`catalog.materials.${material.value}`)}
                                </Button>
                            ))}
                        </Box>
                        {MATERIAL_OPTIONS.length > 15 && (
                            <Typography
                                onClick={() => setShowAllMaterials(!showAllMaterials)}
                                sx={{
                                    fontSize: 11,
                                    color: '#999',
                                    textAlign: 'center',
                                    mt: 0.5,
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                }}
                            >
                                {showAllMaterials ? t('common.collapse') : t('common.more')}
                            </Typography>
                        )}
                    </Box>

                    {/* 착용감 하위 섹션 */}
                    {dimEntities?.dimFit && dimEntities.dimFit.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#333', mb: 1.5 }}>
                                {t('catalog.comfort')}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                }}
                            >
                                {dimEntities.dimFit.map((fit) => (
                                    <Button
                                        key={fit.id}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleFitChange(fit.id)}
                                        sx={{
                                            minWidth: 'auto',
                                            px: 1.2,
                                            py: 0.4,
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: '#222',
                                            textTransform: 'none',
                                            borderColor: selectedFits.includes(fit.id) ? '#222' : '#ddd',
                                            backgroundColor: selectedFits.includes(fit.id) ? '#f5f5f5' : '#fff',
                                            '&:hover': {
                                                borderColor: '#222',
                                                backgroundColor: '#f5f5f5',
                                            },
                                        }}
                                    >
                                        {getDimEntityLabel(fit)}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* 패턴 하위 섹션 */}
                    {dimEntities?.dimPattern && dimEntities.dimPattern.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#333', mb: 1.5 }}>
                                {t('catalog.pattern')}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                }}
                            >
                                {(showAllDimPatterns
                                    ? dimEntities.dimPattern
                                    : dimEntities.dimPattern.slice(0, 12)
                                ).map((pattern) => (
                                    <Button
                                        key={pattern.id}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleDimPatternChange(pattern.id)}
                                        sx={{
                                            minWidth: 'auto',
                                            px: 1.2,
                                            py: 0.4,
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: '#222',
                                            textTransform: 'none',
                                            borderColor: selectedDimPatterns.includes(pattern.id) ? '#222' : '#ddd',
                                            backgroundColor: selectedDimPatterns.includes(pattern.id)
                                                ? '#f5f5f5'
                                                : '#fff',
                                            '&:hover': {
                                                borderColor: '#222',
                                                backgroundColor: '#f5f5f5',
                                            },
                                        }}
                                    >
                                        {getDimEntityLabel(pattern)}
                                    </Button>
                                ))}
                            </Box>
                            {dimEntities.dimPattern.length > 12 && (
                                <Typography
                                    onClick={() => setShowAllDimPatterns(!showAllDimPatterns)}
                                    sx={{
                                        fontSize: 11,
                                        color: '#999',
                                        textAlign: 'center',
                                        mt: 0.5,
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                    }}
                                >
                                    {showAllDimPatterns ? t('common.collapse') : t('common.more')}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* 계절감 하위 섹션 */}
                    {dimEntities?.dimSeason && dimEntities.dimSeason.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#333', mb: 1.5 }}>
                                {t('catalog.season')}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                }}
                            >
                                {dimEntities.dimSeason.map((season) => (
                                    <Button
                                        key={season.id}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleSeasonChange(season.id)}
                                        sx={{
                                            minWidth: 'auto',
                                            px: 1.2,
                                            py: 0.4,
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: '#222',
                                            textTransform: 'none',
                                            borderColor: selectedSeasons.includes(season.id) ? '#222' : '#ddd',
                                            backgroundColor: selectedSeasons.includes(season.id) ? '#f5f5f5' : '#fff',
                                            '&:hover': {
                                                borderColor: '#222',
                                                backgroundColor: '#f5f5f5',
                                            },
                                        }}
                                    >
                                        {getDimEntityLabel(season)}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* 비침 하위 섹션 */}
                    {dimEntities?.dimSheerness && dimEntities.dimSheerness.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#333', mb: 1.5 }}>
                                {t('catalog.sheerness')}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                }}
                            >
                                {dimEntities.dimSheerness.map((sheerness) => (
                                    <Button
                                        key={sheerness.id}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleSheernessChange(sheerness.id)}
                                        sx={{
                                            minWidth: 'auto',
                                            px: 1.2,
                                            py: 0.4,
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: '#222',
                                            textTransform: 'none',
                                            borderColor: selectedSheerness.includes(sheerness.id) ? '#222' : '#ddd',
                                            backgroundColor: selectedSheerness.includes(sheerness.id)
                                                ? '#f5f5f5'
                                                : '#fff',
                                            '&:hover': {
                                                borderColor: '#222',
                                                backgroundColor: '#f5f5f5',
                                            },
                                        }}
                                    >
                                        {getDimEntityLabel(sheerness)}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* 신축성 하위 섹션 */}
                    {dimEntities?.dimStretch && dimEntities.dimStretch.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#333', mb: 1.5 }}>
                                {t('catalog.stretch')}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                }}
                            >
                                {dimEntities.dimStretch.map((stretch) => (
                                    <Button
                                        key={stretch.id}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleStretchChange(stretch.id)}
                                        sx={{
                                            minWidth: 'auto',
                                            px: 1.2,
                                            py: 0.4,
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: '#222',
                                            textTransform: 'none',
                                            borderColor: selectedStretch.includes(stretch.id) ? '#222' : '#ddd',
                                            backgroundColor: selectedStretch.includes(stretch.id) ? '#f5f5f5' : '#fff',
                                            '&:hover': {
                                                borderColor: '#222',
                                                backgroundColor: '#f5f5f5',
                                            },
                                        }}
                                    >
                                        {getDimEntityLabel(stretch)}
                                    </Button>
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* 두께감 하위 섹션 */}
                    {dimEntities?.dimThickness && dimEntities.dimThickness.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#333', mb: 1.5 }}>
                                {t('catalog.thickness')}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                }}
                            >
                                {(showAllDimThickness
                                    ? dimEntities.dimThickness
                                    : dimEntities.dimThickness.slice(0, 12)
                                ).map((style) => (
                                    <Button
                                        key={style.id}
                                        variant="outlined"
                                        size="small"
                                        onClick={() => handleDimThicknessChange(style.id)}
                                        sx={{
                                            minWidth: 'auto',
                                            px: 1.2,
                                            py: 0.4,
                                            fontSize: 14,
                                            fontWeight: 500,
                                            color: '#222',
                                            textTransform: 'none',
                                            borderColor: selectedDimThickness.includes(style.id) ? '#222' : '#ddd',
                                            backgroundColor: selectedDimThickness.includes(style.id)
                                                ? '#f5f5f5'
                                                : '#fff',
                                            '&:hover': {
                                                borderColor: '#222',
                                                backgroundColor: '#f5f5f5',
                                            },
                                        }}
                                    >
                                        {getDimEntityLabel(style)}
                                    </Button>
                                ))}
                            </Box>
                            {dimEntities.dimThickness.length > 12 && (
                                <Typography
                                    onClick={() => setShowAllDimThickness(!showAllDimThickness)}
                                    sx={{
                                        fontSize: 11,
                                        color: '#999',
                                        textAlign: 'center',
                                        mt: 0.5,
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                    }}
                                >
                                    {showAllDimThickness ? t('common.collapse') : t('common.more')}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* 스타일 하위 섹션 */}
                    {dimEntities?.dimStyle && dimEntities.dimStyle.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#333', mb: 1.5 }}>
                                {t('catalog.style')}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 0.5,
                                }}
                            >
                                {(showAllDimStyles ? dimEntities.dimStyle : dimEntities.dimStyle.slice(0, 12)).map(
                                    (style) => (
                                        <Button
                                            key={style.id}
                                            variant="outlined"
                                            size="small"
                                            onClick={() => handleDimStyleChange(style.id)}
                                            sx={{
                                                minWidth: 'auto',
                                                px: 1.2,
                                                py: 0.4,
                                                fontSize: 14,
                                                fontWeight: 500,
                                                color: '#222',
                                                textTransform: 'none',
                                                borderColor: selectedDimStyles.includes(style.id) ? '#222' : '#ddd',
                                                backgroundColor: selectedDimStyles.includes(style.id)
                                                    ? '#f5f5f5'
                                                    : '#fff',
                                                '&:hover': {
                                                    borderColor: '#222',
                                                    backgroundColor: '#f5f5f5',
                                                },
                                            }}
                                        >
                                            {getDimEntityLabel(style)}
                                        </Button>
                                    ),
                                )}
                            </Box>
                            {dimEntities.dimStyle.length > 12 && (
                                <Typography
                                    onClick={() => setShowAllDimStyles(!showAllDimStyles)}
                                    sx={{
                                        fontSize: 11,
                                        color: '#999',
                                        textAlign: 'center',
                                        mt: 0.5,
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                    }}
                                >
                                    {showAllDimStyles ? t('common.collapse') : t('common.more')}
                                </Typography>
                            )}
                        </Box>
                    )}

                    {/* 낱장가능 필터 */}
                    <Box sx={{ mb: 2, borderTop: '1px solid #eee', pt: 2 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#333', mb: 1.5 }}></Typography>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setSingleSheetAvailable(!singleSheetAvailable)}
                            sx={{
                                width: '100%',
                                fontSize: 14,
                                fontWeight: 500,
                                color: '#222',
                                textTransform: 'none',
                                borderColor: singleSheetAvailable ? '#222' : '#ddd',
                                backgroundColor: singleSheetAvailable ? '#f5f5f5' : '#fff',
                                '&:hover': {
                                    borderColor: '#222',
                                    backgroundColor: '#f5f5f5',
                                },
                            }}
                        >
                            {t('catalog.singleSheetAvailable')}
                        </Button>
                    </Box>
                </Collapse>
            </Box>
        </Box>
    );

    return (
        <Box
            sx={{
                width: '100%',
                minWidth: 0,
                maxWidth: 2000,
                mx: 'auto',
                px: { xs: 2, sm: 2, md: '40px', lg: '40px' },
                pt: { xs: '12px', md: '16px' },
                pb: { xs: 2, md: 4 },
                backgroundColor: '#fff',
            }}
        >
            {/* 뒤로가기 플로팅 버튼 - scheduleId가 있고 로그인된 경우에만 표시 */}
            {scheduleId && hasSession && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: '24px',
                        right: '24px',
                        zIndex: 1000,
                    }}
                >
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={handleBackToSchedule}
                        variant="contained"
                        sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: '#fff',
                            backgroundColor: '#1976d2',
                            borderRadius: '28px',
                            px: 3,
                            py: 1.5,
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: '#1565c0',
                                boxShadow: '0 6px 16px rgba(0, 0, 0, 0.2)',
                                transform: 'translateY(-2px)',
                            },
                            transition: 'all 0.2s ease-in-out',
                        }}
                    >
                        {t('catalog.backToSchedule')}
                    </Button>
                </Box>
            )}

            {/* 선택 현황 표시 - 하단 중앙 */}
            {scheduleId && hasSession && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: '24px',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1000,
                        display: 'flex',
                        gap: 2,
                    }}
                >
                    {/* 추가가능 매장 */}
                    <Paper
                        elevation={3}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 3,
                            py: 1.5,
                            borderRadius: '20px',
                            background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        }}
                    >
                        <StorefrontIcon sx={{ fontSize: 24, color: '#fff' }} />
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 500,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    lineHeight: 1.2,
                                }}
                            >
                                추가가능 매장
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 18,
                                    fontWeight: 700,
                                    color: '#fff',
                                    lineHeight: 1.2,
                                }}
                            >
                                {selectedVendor.size}/{selectedVendorLimit}
                            </Typography>
                        </Box>
                    </Paper>

                    {/* 상품 */}
                    <Paper
                        elevation={3}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            px: 3,
                            py: 1.5,
                            borderRadius: '20px',
                            background: 'linear-gradient(135deg, #fbc2eb 0%, #a6c1ee 100%)',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        }}
                    >
                        <ShoppingBagIcon sx={{ fontSize: 24, color: '#fff' }} />
                        <Box>
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontWeight: 500,
                                    color: 'rgba(255, 255, 255, 0.9)',
                                    lineHeight: 1.2,
                                }}
                            >
                                상품
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: 18,
                                    fontWeight: 700,
                                    color: '#fff',
                                    lineHeight: 1.2,
                                }}
                            >
                                {scheduleData?.ddmLiveScheduleProductMappers?.length || 0}/무제한
                            </Typography>
                        </Box>
                    </Paper>
                </Box>
            )}

            {/* 카테고리/전체신상 버튼 영역 */}
            <Box sx={{ position: 'relative', mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button
                        ref={categoryButtonRef}
                        variant="outlined"
                        onMouseEnter={handleCategoryButtonMouseEnter}
                        onMouseLeave={handleCategoryButtonMouseLeave}
                        sx={{
                            color: categoryDropdownOpen ? '#FF0033' : '#222',
                            borderColor: categoryDropdownOpen ? '#FF0033' : '#ddd',
                            backgroundColor: categoryDropdownOpen ? '#fff5f5' : '#fff',
                            fontSize: 16,
                            fontWeight: 500,
                            px: 2,
                            py: 1,
                            minWidth: 'auto',
                            textTransform: 'none',
                            borderRadius: '10px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: '#fff5f5',
                                color: '#FF0033',
                                borderColor: '#FF0033',
                            },
                        }}
                    >
                        {t('navigation.categories')}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={() => navigate('/')}
                        sx={{
                            color: '#222',
                            borderColor: '#ddd',
                            fontSize: 16,
                            fontWeight: 500,
                            px: 2,
                            py: 1,
                            minWidth: 'auto',
                            textTransform: 'none',
                            borderRadius: '10px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                backgroundColor: '#f5f5f5',
                                color: '#FF0033',
                                borderColor: '#FF0033',
                            },
                        }}
                    >
                        {t('navigation.allNew')}
                    </Button>
                </Box>

                {/* 카테고리 드롭다운 */}
                {!isMobile && categoryDropdownOpen && (
                    <Box
                        ref={categoryDropdownRef}
                        onMouseEnter={handleCategoryDropdownMouseEnter}
                        onMouseLeave={handleCategoryDropdownMouseLeave}
                        sx={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            width: '100%',
                            zIndex: 20,
                            pt: 1,
                        }}
                    >
                        <Paper
                            elevation={8}
                            sx={{
                                p: 3,
                                backgroundColor: '#fff',
                                borderRadius: 2,
                                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                                border: '1px solid #f0f0f0',
                            }}
                        >
                            <Box sx={{ display: 'flex', gap: 4 }}>
                                {/* 좌측 성별 카테고리 */}
                                <Box sx={{ minWidth: 120 }}>
                                    {categoryLoading ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                            <Typography sx={{ color: '#666' }}>로딩 중...</Typography>
                                        </Box>
                                    ) : Object.keys(categoryData).length > 0 ? (
                                        Object.entries(categoryData).map(([key, genderData]) => (
                                            <Box
                                                key={key}
                                                sx={{
                                                    py: 1.5,
                                                    px: 2,
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'space-between',
                                                    borderRadius: 1,
                                                    backgroundColor: hoveredGender === key ? '#f5f5f5' : 'transparent',
                                                    transition: 'all 0.2s ease',
                                                    '&:hover': {
                                                        backgroundColor: '#f5f5f5',
                                                    },
                                                }}
                                                onMouseEnter={() => setHoveredGender(key)}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontSize: 16,
                                                        fontWeight: hoveredGender === key ? 700 : 500,
                                                        color: hoveredGender === key ? '#FF0033' : '#222',
                                                    }}
                                                >
                                                    {translateCategoryName(genderData.name, t)}
                                                </Typography>
                                                {hoveredGender === key && (
                                                    <Typography sx={{ color: '#FF0033', fontSize: 16 }}>›</Typography>
                                                )}
                                            </Box>
                                        ))
                                    ) : (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                            <Typography sx={{ color: '#666' }}>
                                                카테고리를 불러올 수 없습니다.
                                            </Typography>
                                        </Box>
                                    )}
                                </Box>

                                {/* 우측 상세 카테고리 */}
                                {hoveredGender && categoryData[hoveredGender] && (
                                    <Box sx={{ flex: 1 }}>
                                        <Grid container spacing={3}>
                                            {categoryData[hoveredGender].categories.map((category, index) => (
                                                <Grid item xs={12} sm={6} md={2.4} key={index}>
                                                    <Card
                                                        sx={{
                                                            height: '100%',
                                                            boxShadow: 'none',
                                                            border: '1px solid #f0f0f0',
                                                            borderRadius: 2,
                                                            transition: 'all 0.2s ease',
                                                            '&:hover': {
                                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                                                            },
                                                        }}
                                                    >
                                                        <CardContent sx={{ p: 2 }}>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    mb: 2,
                                                                    p: 1.5,
                                                                    backgroundColor: '#f8f8f8',
                                                                    borderRadius: 1,
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease',
                                                                    '&:hover': {
                                                                        backgroundColor: '#e8e8e8',
                                                                    },
                                                                }}
                                                                onClick={() => {
                                                                    // URL 업데이트 및 드롭다운 닫기
                                                                    const newUrl = `/?gender=${hoveredGender}&category=${encodeURIComponent(category.name)}`;
                                                                    window.history.pushState({}, '', newUrl);
                                                                    window.dispatchEvent(new PopStateEvent('popstate'));
                                                                    setCategoryDropdownOpen(false);
                                                                }}
                                                            >
                                                                <Box
                                                                    component="img"
                                                                    src={category.icon}
                                                                    alt={category.name}
                                                                    sx={{
                                                                        width: 56,
                                                                        height: 56,
                                                                        mr: 1.5,
                                                                        objectFit: 'contain',
                                                                    }}
                                                                />
                                                                <Typography
                                                                    sx={{
                                                                        fontSize: 14,
                                                                        fontWeight: 700,
                                                                        color: '#222',
                                                                    }}
                                                                >
                                                                    {translateCategoryName(category.name, t)}
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                {category.subcategories.map((subcategory, subIndex) => (
                                                                    <Typography
                                                                        key={subIndex}
                                                                        sx={{
                                                                            fontSize: 13,
                                                                            color: '#333',
                                                                            py: 0.5,
                                                                            cursor: 'pointer',
                                                                            '&:hover': {
                                                                                color: '#FF0033',
                                                                            },
                                                                        }}
                                                                        onClick={() => {
                                                                            // URL 업데이트 및 드롭다운 닫기
                                                                            const newUrl = `/?gender=${hoveredGender}&category=${encodeURIComponent(category.name)}&subcategory=${encodeURIComponent(subcategory)}`;
                                                                            window.history.pushState({}, '', newUrl);
                                                                            window.dispatchEvent(
                                                                                new PopStateEvent('popstate'),
                                                                            );
                                                                            setCategoryDropdownOpen(false);
                                                                        }}
                                                                    >
                                                                        {translateCategoryName(subcategory, t)}
                                                                    </Typography>
                                                                ))}
                                                            </Box>
                                                        </CardContent>
                                                    </Card>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                )}
                            </Box>
                        </Paper>
                    </Box>
                )}
            </Box>
            {/* 브레드크럼 + 현재 카테고리 + 상품 개수 통합 영역 */}
            <Box
                sx={{
                    mb: 1.5,
                    pb: 1.5,
                    borderBottom: '2px solid #222',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    flexWrap: 'wrap',
                }}
            >
                {/* 현재 카테고리 제목 */}
                <Typography
                    variant="h4"
                    sx={{
                        fontSize: { xs: 20, md: 26 },
                        fontWeight: 700,
                        color: '#222',
                        flex: '0 0 auto',
                    }}
                >
                    {(() => {
                        // 4depth 카테고리가 선택된 경우
                        if (currentSubSubCategories.length > 0 && selectedSubSubTab > 0) {
                            return translateCategoryName(
                                currentSubSubCategories[selectedSubSubTab - 1]?.categoryName,
                                t,
                            );
                        }
                        // 3depth 카테고리가 선택된 경우
                        if (currentSubCategories.length > 0 && selectedSubTab > 0) {
                            return translateCategoryName(currentSubCategories[selectedSubTab - 1]?.categoryName, t);
                        }
                        // 2depth 카테고리가 선택된 경우
                        if (selectedTab > 0) {
                            return translateCategoryName(subCategories[selectedTab - 1]?.categoryName, t);
                        }
                        // 전체 (성별)
                        return selectedGender === 'woman'
                            ? t('catalog.women')
                            : selectedGender === 'men'
                              ? t('catalog.men')
                              : t('catalog.kids');
                    })()}
                </Typography>
                {/* 브레드크럼 */}
                <Breadcrumbs
                    separator={<NavigateNextIcon fontSize="small" sx={{ color: '#bbb' }} />}
                    sx={{
                        mt: '8px',
                        ml: '10px',
                        '& .MuiBreadcrumbs-separator': {
                            mx: 0.5,
                        },
                    }}
                >
                    <Typography
                        component="span"
                        sx={{
                            fontSize: 13,
                            color: '#999',
                            cursor: 'pointer',
                            textDecoration: 'underline',
                            '&:hover': {
                                color: '#666',
                            },
                        }}
                        onClick={() => {
                            setSelectedTab(0);
                            setCurrentSubCategories([]);
                            setSelectedSubTab(0);
                            setCurrentSubSubCategories([]);
                            setSelectedSubSubTab(0);
                        }}
                    >
                        {selectedGender === 'woman'
                            ? t('catalog.women')
                            : selectedGender === 'men'
                              ? t('catalog.men')
                              : t('catalog.kids')}
                    </Typography>
                    {selectedTab > 0 && subCategories[selectedTab - 1] && (
                        <Typography
                            component="span"
                            sx={{
                                fontSize: 13,
                                color: '#999',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                '&:hover': {
                                    color: '#666',
                                },
                            }}
                            onClick={() => {
                                // 해당 메인 카테고리의 하위 카테고리들을 다시 로드
                                const clickedCategory = subCategories[selectedTab - 1];
                                if (clickedCategory) {
                                    const subCategoriesOfClicked =
                                        allCategories
                                            ?.filter((cat) => cat.parentCategoryId === clickedCategory.categoryId)
                                            .sort((a, b) => a.sortOrder - b.sortOrder) || [];
                                    setCurrentSubCategories(subCategoriesOfClicked);
                                } else {
                                    setCurrentSubCategories([]);
                                }
                                setSelectedSubTab(0);
                                setCurrentSubSubCategories([]);
                                setSelectedSubSubTab(0);
                            }}
                        >
                            {translateCategoryName(subCategories[selectedTab - 1].categoryName, t)}
                        </Typography>
                    )}
                    {/* 3depth 카테고리 */}
                    {selectedSubTab > 0 && currentSubCategories[selectedSubTab - 1] && (
                        <Typography
                            component="span"
                            sx={{
                                fontSize: 13,
                                color: '#999',
                                cursor: 'pointer',
                                textDecoration: 'underline',
                                '&:hover': {
                                    color: '#666',
                                },
                            }}
                            onClick={() => {
                                // 해당 서브 카테고리의 하위 카테고리들을 다시 로드
                                const clickedSubCategory = currentSubCategories[selectedSubTab - 1];
                                if (clickedSubCategory) {
                                    const subSubCategoriesOfClicked =
                                        allCategories
                                            ?.filter((cat) => cat.parentCategoryId === clickedSubCategory.categoryId)
                                            .sort((a, b) => a.sortOrder - b.sortOrder) || [];
                                    setCurrentSubSubCategories(subSubCategoriesOfClicked);
                                } else {
                                    setCurrentSubSubCategories([]);
                                }
                                setSelectedSubSubTab(0);
                            }}
                        >
                            {translateCategoryName(currentSubCategories[selectedSubTab - 1].categoryName, t)}
                        </Typography>
                    )}
                    {/* 4depth 카테고리 - 전체 선택이 아닐 때만 마지막 브레드크럼 항목 표시 */}
                    {selectedSubSubTab > 0 && (
                        <Typography sx={{ fontSize: 13, color: '#999', fontWeight: 400 }}>
                            {translateCategoryName(currentSubSubCategories[selectedSubSubTab - 1]?.categoryName, t)}
                        </Typography>
                    )}
                </Breadcrumbs>

                {/* 상품 개수 */}
                <Typography sx={{ fontSize: { xs: 13, md: 14 }, color: '#666', fontWeight: 400, ml: 'auto' }}>
                    {(productTotal || 0).toLocaleString()}
                    {t('catalog.productCount').replace('{{count}}', '').trim()}
                </Typography>
            </Box>

            {/* 카테고리 탭 (1줄로 교체) */}
            <Box sx={{ mb: 2.5 }}>
                <Tabs
                    value={(() => {
                        // 4depth 카테고리가 있을 때
                        if (currentSubSubCategories.length > 0) {
                            return selectedSubSubTab;
                        }
                        // 3depth 카테고리가 있을 때
                        if (currentSubCategories.length > 0) {
                            return selectedSubTab;
                        }
                        // 2depth 카테고리 (메인)
                        return selectedTab;
                    })()}
                    onChange={(_: React.SyntheticEvent, v: number) => {
                        try {
                            // 4depth 카테고리 선택
                            if (currentSubSubCategories.length > 0) {
                                if (v === 0) {
                                    // 전체 선택 시 3depth로 돌아가기
                                    const currentSubCategory = currentSubCategories[selectedSubTab - 1];
                                    if (currentSubCategory) {
                                        const subSubCategoriesOfSub =
                                            allCategories
                                                ?.filter(
                                                    (cat) => cat.parentCategoryId === currentSubCategory.categoryId,
                                                )
                                                .sort((a, b) => a.sortOrder - b.sortOrder) || [];
                                        setCurrentSubSubCategories(subSubCategoriesOfSub);
                                    } else {
                                        setCurrentSubSubCategories([]);
                                    }
                                    setSelectedSubSubTab(0);
                                } else {
                                    handleSubSubCategoryClick(v);
                                }
                            } else if (currentSubCategories.length > 0) {
                                // 3depth 카테고리 선택
                                if (v === 0) {
                                    // 전체 선택 시 해당 메인 카테고리의 하위 카테고리들을 다시 로드
                                    const currentMainCategory = subCategories[selectedTab - 1];
                                    if (currentMainCategory) {
                                        const subCategoriesOfMain =
                                            allCategories
                                                ?.filter(
                                                    (cat) => cat.parentCategoryId === currentMainCategory.categoryId,
                                                )
                                                .sort((a, b) => a.sortOrder - b.sortOrder) || [];
                                        setCurrentSubCategories(subCategoriesOfMain);
                                    } else {
                                        setCurrentSubCategories([]);
                                    }
                                    setSelectedSubTab(0);
                                    setCurrentSubSubCategories([]);
                                    setSelectedSubSubTab(0);
                                } else {
                                    handleSubCategoryClick(v);
                                }
                            } else {
                                // 2depth 카테고리 선택
                                if (v === 0) {
                                    setSelectedTab(v);
                                    setCurrentSubCategories([]);
                                    setSelectedSubTab(0);
                                    setCurrentSubSubCategories([]);
                                    setSelectedSubSubTab(0);
                                } else if (subCategories && subCategories[v - 1]) {
                                    handleCategoryClick(subCategories[v - 1], v);
                                }
                            }
                        } catch (error) {
                            console.error('Error in category tab onChange:', error);
                        }
                    }}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{
                        minHeight: 48,
                        '& .MuiTabs-indicator': {
                            display: 'none',
                        },
                        '& .MuiTab-root': {
                            fontSize: 14,
                            fontWeight: 400,
                            color: '#555',
                            minHeight: 48,
                            px: 2,
                            minWidth: 'auto',
                            textTransform: 'none',
                            '&.Mui-selected': {
                                color: '#222',
                                fontWeight: 700,
                            },
                        },
                    }}
                >
                    {/* 전체 탭 */}
                    <Tab label={t('common.all')} />

                    {/* 현재 depth에 따른 카테고리들 */}
                    {categoriesLoading ? (
                        <Tab label={t('common.loading')} disabled />
                    ) : currentSubSubCategories.length > 0 ? (
                        // 4depth 카테고리 표시
                        currentSubSubCategories.map((category) => (
                            <Tab
                                key={category.categoryId}
                                label={translateCategoryName(category.categoryName, t)}
                                data-category-id={category.categoryId}
                            />
                        ))
                    ) : currentSubCategories.length > 0 ? (
                        // 3depth 카테고리 표시
                        currentSubCategories.map((category) => (
                            <Tab
                                key={category.categoryId}
                                label={translateCategoryName(category.categoryName, t)}
                                data-category-id={category.categoryId}
                            />
                        ))
                    ) : (
                        // 2depth 카테고리 표시
                        subCategories.map((category) => (
                            <Tab
                                key={category.categoryId}
                                label={translateCategoryName(category.categoryName, t)}
                                data-category-id={category.categoryId}
                            />
                        ))
                    )}
                </Tabs>
            </Box>

            {/* 메인 컨텐츠 영역 */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                {/* 왼쪽 사이드바 (PC만) */}
                {renderSidebar()}

                {/* 상품 그리드 */}
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0, // flex item이 overflow하지 않도록
                    }}
                >
                    {/* 검색 키워드 표시 */}
                    {appliedSearchKeywords.length > 0 && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 2,
                                flexWrap: 'wrap',
                            }}
                        >
                            {appliedSearchKeywords.map((keyword, index) => (
                                <Box
                                    key={index}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5,
                                        px: 1.5,
                                        py: 0.5,
                                        backgroundColor: '#fff',
                                        border: '1px solid #ddd',
                                        borderRadius: '20px',
                                    }}
                                >
                                    <Typography sx={{ fontSize: 14, color: '#222' }}>{keyword}</Typography>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            setAppliedSearchKeywords(
                                                appliedSearchKeywords.filter((_, i) => i !== index),
                                            );
                                        }}
                                        sx={{
                                            p: 0,
                                            width: 16,
                                            height: 16,
                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                            },
                                        }}
                                    >
                                        <CloseIcon sx={{ fontSize: 14, color: '#222' }} />
                                    </IconButton>
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'repeat(2, 1fr)',
                                sm: 'repeat(4, 1fr)',
                                md: 'repeat(5, 1fr)',
                            },
                            gap: { xs: 1.5, md: 2.5 },
                        }}
                    >
                        {products.map((apiProduct) => {
                            const product = transformApiToUiProduct(apiProduct, t, i18n.language);
                            const isSelected = scheduledPidSet.has(product.id);
                            const isFavorite = favorites.has(product.id);

                            return (
                                <Card
                                    key={product.id}
                                    sx={{
                                        borderRadius: 0,
                                        boxShadow: 'none',
                                        overflow: 'hidden',
                                        cursor: 'pointer',
                                        backgroundColor: '#fff',
                                        width: '100%',
                                        minWidth: 0,
                                        minHeight: 'fit-content',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        '&:hover': {
                                            '& .product-image': {
                                                transform: 'scale(1.05)',
                                            },
                                        },
                                    }}
                                    onClick={() => handleOpen(apiProduct)}
                                >
                                    <Box
                                        sx={{
                                            position: 'relative',
                                            overflow: 'hidden',
                                            borderRadius: 0,
                                            mb: 1,
                                            width: '100%',
                                            height: 0,
                                            paddingBottom: '133.33%', // 3:4 비율 (4/3 * 100%)
                                            flexShrink: 0,
                                            backgroundColor: '#f5f5f5',
                                        }}
                                    >
                                        <CardMedia
                                            component="img"
                                            image={product.image}
                                            alt={product.title}
                                            className="product-image"
                                            loading="lazy"
                                            sx={{
                                                position: 'absolute',
                                                top: 0,
                                                left: 0,
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                transition: 'transform 0.3s ease',
                                                backgroundColor: '#f5f5f5',
                                                display: 'block',
                                            }}
                                            onLoad={(e: React.SyntheticEvent<HTMLImageElement>) => {
                                                // 이미지 로드 완료 시 배경색 제거
                                                const target = e.target as HTMLImageElement;
                                                target.style.backgroundColor = 'transparent';
                                            }}
                                        />
                                        {/* 하트 아이콘 */}
                                        <IconButton
                                            onClick={(e: React.MouseEvent) => toggleFavorite(product.id, e)}
                                            sx={{
                                                position: 'absolute',
                                                left: 8,
                                                bottom: 8,
                                                width: 28,
                                                height: 28,
                                                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                                                '&:hover': {
                                                    backgroundColor: 'rgba(255, 255, 255, 1)',
                                                },
                                            }}
                                        >
                                            {isFavorite ? (
                                                <FavoriteIcon sx={{ fontSize: 16, color: '#FF0033' }} />
                                            ) : (
                                                <FavoriteBorderIcon sx={{ fontSize: 16, color: '#666' }} />
                                            )}
                                        </IconButton>
                                    </Box>
                                    <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
                                        <Typography
                                            sx={{
                                                fontSize: { xs: 12, md: 13 },
                                                color: '#222',
                                                fontWeight: 400,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                mb: 0.3,
                                            }}
                                        >
                                            {product.title}
                                        </Typography>
                                        {(() => {
                                            const storeLocation = apiProduct.vendor?.vendorBusinessInfo?.storeLocation;
                                            const storeName = apiProduct.vendor?.vendorBusinessInfo?.storeName;

                                            // Store 아이콘 컴포넌트
                                            const StoreIcon = () => (
                                                <img
                                                    src={storeIcon}
                                                    alt="store"
                                                    style={{
                                                        width: '14px',
                                                        height: '14px',
                                                        display: 'inline-block',
                                                        verticalAlign: 'middle',
                                                        marginRight: '4px',
                                                    }}
                                                />
                                            );

                                            // Building 아이콘 컴포넌트
                                            const BuildingIcon = () => (
                                                <img
                                                    src={buildingIcon}
                                                    alt="building"
                                                    style={{
                                                        width: '14px',
                                                        height: '14px',
                                                        display: 'inline-block',
                                                        verticalAlign: 'middle',
                                                        marginRight: '4px',
                                                    }}
                                                />
                                            );

                                            // 위치 정보가 있을 때만 표시
                                            if (storeLocation && storeName) {
                                                // parseStoreLocation을 사용하여 건물명 추출
                                                const parsed = parseStoreLocation(storeLocation);
                                                const buildingName = parsed.building;

                                                // 건물명이 있으면 언어팩 적용, 없으면 원래 로직 사용
                                                const displayBuilding = buildingName
                                                    ? getBuildingName(buildingName)
                                                    : storeLocation.split(',')[0];

                                                return (
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            fontSize: { xs: 11, md: 12 },
                                                            fontWeight: 700,
                                                            color: '#999',
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        <BuildingIcon />
                                                        <Typography
                                                            component="span"
                                                            sx={{
                                                                fontSize: 'inherit',
                                                                color: 'inherit',
                                                            }}
                                                        >
                                                            {displayBuilding}
                                                        </Typography>
                                                        <Typography
                                                            component="span"
                                                            sx={{
                                                                fontSize: 'inherit',
                                                                color: 'inherit',
                                                                mx: 0.5,
                                                            }}
                                                        >
                                                            /
                                                        </Typography>
                                                        <StoreIcon />
                                                        <Typography
                                                            component="span"
                                                            sx={{
                                                                fontSize: 'inherit',
                                                                color: 'inherit',
                                                            }}
                                                        >
                                                            {storeName}
                                                        </Typography>
                                                    </Box>
                                                );
                                            }
                                            // 위치 정보가 없으면 아무것도 표시하지 않음
                                            return null;
                                        })()}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: { xs: 13, md: 15 },
                                                    fontWeight: 700,
                                                    color: '#222',
                                                }}
                                            >
                                                {userId
                                                    ? `₩${product.price.toLocaleString()}`
                                                    : t('catalog.memberOnly')}
                                            </Typography>
                                        </Box>

                                        {/* 액션 버튼 영역 */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                flexDirection: { xs: 'column', xl: 'row' },
                                                gap: 0.5,
                                                mt: 1,
                                            }}
                                        >
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={isSelected ? <EventAvailableIcon /> : <CalendarMonthIcon />}
                                                onClick={async (e) => {
                                                    e.stopPropagation();

                                                    // 로그인 체크
                                                    const isLoggedIn = checkLoginAndShowModal(async () => {
                                                        // 로그인 성공 후 페이지 새로고침
                                                        window.location.reload();
                                                    });

                                                    if (!isLoggedIn) {
                                                        return;
                                                    }

                                                    handleScheduleAction(product.id, isSelected);
                                                }}
                                                sx={{
                                                    flex: { xs: 'none', xl: 1 },
                                                    width: { xs: '100%', xl: 'auto' },
                                                    fontSize: { xs: 10, sm: 11, md: 12 },
                                                    fontWeight: 500,
                                                    color: isSelected ? '#1976d2' : '#222',
                                                    borderColor: isSelected ? '#1976d2' : '#ddd',
                                                    backgroundColor: isSelected ? '#e3f2fd' : 'transparent',
                                                    textTransform: 'none',
                                                    py: 0.5,
                                                    px: 1,
                                                    minWidth: 0,
                                                    whiteSpace: { xs: 'normal', xl: 'nowrap' },
                                                    overflow: 'hidden',
                                                    textOverflow: { xs: 'clip', xl: 'ellipsis' },
                                                    height: 'auto',
                                                    minHeight: 32,
                                                    '& .MuiButton-startIcon': {
                                                        mr: 0.5,
                                                        '& > svg': {
                                                            fontSize: { xs: 14, sm: 16, md: 18 },
                                                        },
                                                    },
                                                    '&:hover': {
                                                        borderColor: isSelected ? '#1565c0' : '#222',
                                                        backgroundColor: isSelected ? '#bbdefb' : '#f5f5f5',
                                                    },
                                                }}
                                            >
                                                {isSelected
                                                    ? t('catalog.removeFromLiveSchedule')
                                                    : t('catalog.addToLiveSchedule')}
                                            </Button>
                                            <Tooltip title={t('catalog.featureInProgress')} arrow placement="top">
                                                <Box
                                                    sx={{
                                                        flex: { xs: 'none', xl: 1 },
                                                        width: { xs: '100%', xl: 'auto' },
                                                    }}
                                                >
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        startIcon={<LocalShippingIcon />}
                                                        disabled={hasSession}
                                                        onClick={(e) => {
                                                            e.stopPropagation();

                                                            // 로그인 체크
                                                            const isLoggedIn = checkLoginAndShowModal(() => {});

                                                            if (!isLoggedIn) return;
                                                        }}
                                                        sx={{
                                                            width: '100%',
                                                            fontSize: { xs: 10, sm: 11, md: 12 },
                                                            fontWeight: 500,
                                                            color: hasSession ? '#999 !important' : '#666',
                                                            borderColor: hasSession ? '#e0e0e0 !important' : '#ddd',
                                                            textTransform: 'none',
                                                            py: 0.5,
                                                            px: 1,
                                                            minWidth: 0,
                                                            backgroundColor: hasSession ? '#f9f9f9 !important' : '#fff',
                                                            cursor: hasSession ? 'not-allowed !important' : 'pointer',
                                                            whiteSpace: { xs: 'normal', xl: 'nowrap' },
                                                            overflow: 'hidden',
                                                            textOverflow: { xs: 'clip', xl: 'ellipsis' },
                                                            height: 'auto',
                                                            minHeight: 32,
                                                            '& .MuiButton-startIcon': {
                                                                mr: 0.5,
                                                                '& > svg': {
                                                                    fontSize: { xs: 14, sm: 16, md: 18 },
                                                                },
                                                            },
                                                            '&:hover': {
                                                                borderColor: hasSession ? '#e0e0e0' : '#999',
                                                                backgroundColor: hasSession ? '#f9f9f9' : '#f0f0f0',
                                                            },
                                                        }}
                                                    >
                                                        {t('catalog.sampleRequest')}
                                                    </Button>
                                                </Box>
                                            </Tooltip>
                                        </Box>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Box>

                    {/* 무한 스크롤 sentinel */}
                    <Box
                        ref={loadMoreTriggerRef}
                        sx={{
                            width: '100%',
                            height: 1,
                        }}
                    />

                    {/* 로딩 중 메시지 - 스크롤이 맨 밑에 닿았을 때만 표시 */}
                    {loading && page > 1 && isBottomReached && (
                        <Box
                            sx={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                zIndex: 1000,
                                pointerEvents: 'none',
                            }}
                        >
                            <img src={ddmLoadingGif} alt="Loading..." style={{ width: '120px', height: '120px' }} />
                        </Box>
                    )}

                    {/* 조회 결과 없음 메시지 */}
                    {!loading && products.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 8 }}>
                            <Box sx={{ mb: 2 }}>
                                <Typography sx={{ fontSize: 48, color: '#ddd', mb: 1 }}>🔍</Typography>
                            </Box>
                            <Typography sx={{ fontSize: 18, fontWeight: 600, color: '#333', mb: 1 }}>
                                {t('catalog.noSearchResults')}
                            </Typography>
                            <Typography sx={{ fontSize: 14, color: '#666', mb: 3 }}>
                                {t('catalog.tryDifferentConditions')}
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={() => {
                                    // 필터 초기화
                                    setSelectedMaterials([]);
                                    setSelectedPatterns([]);
                                    setSelectedStyles([]);
                                    setSelectedColors([]);
                                    setSelectedCountries([]);
                                    setSelectedSizesAlpha([]);
                                    setSelectedSizesNumeric([]);
                                    setSelectedDetails([]);
                                    setSelectedFits([]);
                                    setSelectedDimPatterns([]);
                                    setSelectedSeasons([]);
                                    setSelectedSheerness([]);
                                    setSelectedStretch([]);
                                    setSelectedDimStyles([]);
                                    setBuildingFloorSelections({});
                                    setExpandedBuildingFloors(null);
                                    setPriceRange('all');
                                    setCustomMinPrice('');
                                    setCustomMaxPrice('');
                                    setSelectedTab(0);
                                    setCurrentSubCategories([]);
                                    setSelectedSubTab(0);
                                    setCurrentSubSubCategories([]);
                                    setSelectedSubSubTab(0);
                                }}
                                sx={{
                                    px: 3,
                                    py: 1,
                                    fontSize: 14,
                                    borderColor: '#ddd',
                                    color: '#666',
                                    '&:hover': {
                                        borderColor: '#999',
                                        backgroundColor: '#f9f9f9',
                                    },
                                }}
                            >
                                {t('catalog.resetFilters')}
                            </Button>
                        </Box>
                    )}
                </Box>
            </Box>

            <ProductModal open={open} onClose={handleClose} product={selectedProduct} />
        </Box>
    );
};

export default RootCatalog;
